import {
  generateTabId,
  isMobile,
  getCookie,
  getSessionIdFromCookie,
  setCookie,
  deleteCookie,
  setSessionIdCookie,
  deleteSessionIdCookie,
  getTabReconnectAttempts,
  setTabReconnectAttempts,
  debounce,
  formatTime,
  generateId,
  escapeHtml,
  fixOrderedListNumbering,
} from "./modules/utils.js";
import { WebSocketManager } from "./modules/websocket-manager.js";
import { MessageHandler } from "./modules/message-handler.js";
import { ChatLayout } from "./modules/chat-layout.js";
import { ConfigManager } from "./modules/config-manager.js";
import { AudioManager } from "./modules/audio-manager.js";
import { FormManager } from "./modules/form-manager.js";
import { StateManager } from "./modules/state-manager.js";
import { GTMTracker } from "./modules/gtm-tracker.js";
import { ClientLogger } from "./modules/client-logger.js";
import {
  getActiveCtmVisitorSid,
  getOrCreateCtmVisitorSid,
  hasCtmMarketingConsent,
  normalizeCtmVisitorSid,
  observeActiveCtmVisitorSid,
  waitForActiveCtmVisitorSid,
} from "./modules/ctm-tracking.js";
import {
  capturePageContext,
  filterLinksByNavigationPolicy,
  validateNavigationTarget,
} from "./modules/page-tools.js";
import { ensureClientTurnId } from "./modules/client-turn.js";

const ACTIVE_WIDGET_KEY = Symbol.for("ctm-chat.active-widget");
const ACTIVE_PREVIEW_WIDGET_KEY = Symbol.for("ctm-chat.active-preview-widget");
const CLIENT_TOOL_NAVIGATION_DELAY_MS = 100;
const SUGGESTION_DEDUPE_WINDOW_MS = 1000;

class ChatWidget extends HTMLElement {
  static VERSION = "1.0.8";
  static observedAttributes = ["token", "debug", "preview"];

  constructor() {
    super();
    this.logger = new ClientLogger("warn");
    this.attachShadow({ mode: "open" });

    // IOS keyboard – reference to visualViewport handler so we can un‑register
    this._viewportHandler = null;

    // Message Handler
    this.messageHandler = null;
    this.marked = null; // Markdown parser instance
    this.DOMPurify = null; // HTML sanitizer instance

    this.style.visibility = "hidden";

    this.configManager = new ConfigManager(this.logger);
    this.config = this.configManager.get(); // Keep reference for compatibility

    this.stateManager = new StateManager(this.logger);

    this.audioManager = new AudioManager(this.logger);

    this.formManager = new FormManager();

    // Enabled in connectedCallback after preview mode can be determined.
    this.gtmTracker = new GTMTracker(this.logger, { enabled: false });

    this.wsManager = null;
    this.tabId = generateTabId(); // Unique ID for this tab instance
    this.ctmVisitorSid = null;

    this.pendingFiles = []; // Store files pending upload
    this._resumeRequested = false;
    this._ownsPageWidgetSlot = false;
    this._messageSendInFlight = false;
    this._recentSuggestionTurns = new Map();

    // Debounce resize handler
    this.debounceResize = null;

    // Suggestion re-evaluation timer
    this.suggestionReevaluationTimer = null;
    this.currentSuggestions = [];

    // Debug mode - secret multi-click counter
    this.debugClickCount = 0;
    this.debugClickTimer = null;

    this.layout = new ChatLayout(this, this.shadowRoot, this.logger);
    this.layout.render();
  }

  async connectedCallback() {
    if (!this.claimPageWidgetSlot()) {
      return;
    }

    this.gtmTracker.setEnabled(!this.isPreviewMode());
    console.log(`[CTM Chat] v${ChatWidget.VERSION}`);
    if (this.hasAttribute("debug") || this.getAttribute("debug") === "true") {
      this.logger.setLevel("debug");
    }
    if (!this.isPreviewMode()) {
      // Ensure proper mobile viewport scaling for customer-facing embeds.
      this.ensureMobileViewport();
      this.setupKeyboardDetection();
    }

    // Load markdown library first to ensure it's available for rendering
    await this.loadMarkdownLibrary();
    if (!this._ownsPageWidgetSlot) {
      return;
    }
    await this.initWidget();
  }

  disconnectedCallback() {
    if (!this._ownsPageWidgetSlot) {
      this.style.visibility = "hidden";
      return;
    }

    this.releasePageWidgetSlot();
    this.deactivateWidget();
  }

  getWidgetIdentifier() {
    const token = this.getAttribute("token");
    if (!token) {
      return "unknown";
    }

    try {
      const decoded = this.#decodeJWT(token);
      const parts = [decoded.r, decoded.a, decoded.i].filter(
        (part) => part !== undefined && part !== null && part !== "",
      );
      return parts.length > 0 ? parts.join(".") : "unknown";
    } catch (error) {
      return "invalid-token";
    }
  }

  isPreviewMode() {
    return this.hasAttribute?.("preview") || false;
  }

  isContainedPlacement() {
    return this.getAttribute?.("placement") === "contained";
  }

  isNaturalPlacement() {
    return this.getAttribute?.("placement") === "natural";
  }

  isPositionedPlacement() {
    const placement = this.getAttribute?.("placement");
    return placement === "contained" || placement === "natural";
  }

  getPageWidgetSlotKey() {
    return this.isPreviewMode() ? ACTIVE_PREVIEW_WIDGET_KEY : ACTIVE_WIDGET_KEY;
  }

  getSessionCookieName() {
    return this.isPreviewMode()
      ? `${StateManager.SESSION_COOKIE_NAME}preview`
      : StateManager.SESSION_COOKIE_NAME;
  }

  clearPreviewSession() {
    if (!this.isPreviewMode()) return;

    const sessionCookieName = this.getSessionCookieName();
    deleteSessionIdCookie(sessionCookieName);
    deleteCookie(`${sessionCookieName}_active`);
    deleteCookie(`${sessionCookieName}_ended`);
    this.stateManager.set("sessionId", null);
    this.stateManager.set("sessionToken", null);
    this.stateManager.set("sessionTokenExpiry", null);
    this.stateManager.set("durableObjectId", null);
  }

  claimPageWidgetSlot() {
    const widgetSlotKey = this.getPageWidgetSlotKey();
    const activeWidget = globalThis[widgetSlotKey];
    if (
      !activeWidget ||
      activeWidget === this ||
      !activeWidget.isConnected
    ) {
      globalThis[widgetSlotKey] = this;
      this._ownsPageWidgetSlot = true;
      this.removeAttribute("data-ctm-chat-suppressed");
      return true;
    }

    const activeIdentifier = activeWidget.getWidgetIdentifier?.() || "unknown";
    const newIdentifier = this.getWidgetIdentifier();
    const isPrimary = this.hasAttribute("primary");
    const activeIsPrimary = activeWidget.hasAttribute?.("primary") || false;

    if (isPrimary && !activeIsPrimary) {
      console.warn(
        `[CTM Chat] Multiple <ctm-chat> widgets detected. Primary widget "${newIdentifier}" is replacing and suppressing widget "${activeIdentifier}".`,
      );
      activeWidget.suppressForPrimaryWidget?.();
      globalThis[widgetSlotKey] = this;
      this._ownsPageWidgetSlot = true;
      this.removeAttribute("data-ctm-chat-suppressed");
      return true;
    }

    console.warn(
      `[CTM Chat] Multiple <ctm-chat> widgets detected. Keeping widget "${activeIdentifier}" and suppressing widget "${newIdentifier}". Add the primary attribute to the intended widget to select it explicitly.`,
    );
    this._ownsPageWidgetSlot = false;
    this.setAttribute("data-ctm-chat-suppressed", "true");
    this.style.visibility = "hidden";
    return false;
  }

  suppressForPrimaryWidget() {
    this._ownsPageWidgetSlot = false;
    this.setAttribute("data-ctm-chat-suppressed", "true");
    this.style.visibility = "hidden";
    this.deactivateWidget();
  }

  releasePageWidgetSlot() {
    const widgetSlotKey = this.getPageWidgetSlotKey();
    if (globalThis[widgetSlotKey] === this) {
      delete globalThis[widgetSlotKey];
    }
    this._ownsPageWidgetSlot = false;
  }

  deactivateWidget() {
    this.teardownKeyboardDetection();
    this.layout?.teardownNaturalPopover();
    this.style.visibility = "hidden";

    const scheduleVisibility = this.stateManager.get("scheduleVisibility");
    if (scheduleVisibility?.checkInterval) {
      clearTimeout(scheduleVisibility.checkInterval);
    }

    // Clean up listeners
    this.bubbleEl?.removeEventListener("click", this.toggleChat);
    this.sendBtnEl?.removeEventListener("click", this.handleSendMessage);
    this.inputEl?.removeEventListener("keypress", this.handleInputKeypress);
    this.inputEl?.removeEventListener("input", this.handleInput); // For enabling/disabling send button
    this.optionsToggleBtn?.removeEventListener("click", this.toggleOptionsMenu);
    this.mobileCloseBtn?.removeEventListener("click", this.toggleChat);

    this.endChatBtn?.removeEventListener("click", this.handleEndChat);
    this.copyEvalBtn?.removeEventListener("click", this.copyAsEval);
    this.attachBtnEl?.removeEventListener("click", this.handleAttachClick);
    this.fileInputEl?.removeEventListener("change", this.handleFileSelect);
    this.messagesContainerEl?.removeEventListener(
      "dragover",
      this.handleDragOver,
    );
    this.messagesContainerEl?.removeEventListener("drop", this.handleDrop);
    if (this.handleOutsideClick) {
      document.removeEventListener("click", this.handleOutsideClick, true);
    }
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize); // Remove resize listener
      window.removeEventListener("orientationchange", this.handleResize);
    }
    if (this.handleVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
    }

    // Clean up timers
    clearTimeout(this.timerShowEngage);
    clearTimeout(this.timerHideEngage);
    clearTimeout(this.debounceResize);
    clearTimeout(this.suggestionReevaluationTimer);

    // Clean up WebSocket
    if (this.wsManager) {
      this.wsManager.disconnect(1000, "Widget disconnected");
    }

    this.stateManager.set("connectionStatus", "disconnected");

    this.audioManager
      ?.cleanup()
      .catch((e) => this.logger.warn("Error cleaning up audio", e));
    this.formManager?.cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    let triggerConfigFetch = false;

    switch (name) {
      case "token":
        if (this.config.token !== newValue) {
          this.updateToken();
          triggerConfigFetch = true;
        }
        break;
      case "debug":
        this.logger.setLevel(newValue === "false" ? "warn" : "debug");
        break;
      case "preview":
        this.gtmTracker?.setEnabled(newValue === null);
        break;
    }

    if (this.stateManager.get("ready")) {
      if (triggerConfigFetch) {
        this.fetchConfig();
      }
    }
  }

  #base64urlDecode(str) {
    // Replace '-' with '+', '_' with '/' and pad with '='
    str = str.replace(/-/g, "+").replace(/_/g, "/");
    while (str.length % 4) str += "=";
    return atob(str);
  }

  #decodeJWT(token) {
    const [headerB64, payloadB64] = token.split("."); // ignore signature
    const header = JSON.parse(this.#base64urlDecode(headerB64));
    const payload = JSON.parse(this.#base64urlDecode(payloadB64));
    return payload;
  }

  updateToken() {
    const token = this.getAttribute("token");
    this.token = this.#decodeJWT(token);

    // Update configuration with token data
    this.configManager.updateFromToken(this.token);
    this.config = this.configManager.get(); // Update local reference

    // Store these for media URL fetching
    this.region = this.token.r;
    this.accountId = this.config.accountId;
  }

  async initWidget() {
    const sessionCookieName = this.getSessionCookieName();
    let sessionId = getSessionIdFromCookie(sessionCookieName);
    const endedCookieName = `${sessionCookieName}_ended`;
    const hasEndedSession = getCookie(endedCookieName) === "true";

    if (sessionId && hasEndedSession) {
      deleteSessionIdCookie(sessionCookieName);
      deleteCookie(endedCookieName);
      deleteCookie(`${sessionCookieName}_active`);
      sessionId = null;
      this.stateManager.set("sessionToken", null);
      this.stateManager.set("sessionTokenExpiry", null);
      this.stateManager.set("durableObjectId", null);
      this.stateManager.set("connectionStatus", "disconnected");
    }

    this.stateManager.set("sessionId", sessionId);

    this.updateToken();
    if (!this.isPreviewMode()) {
      observeActiveCtmVisitorSid(
        this.config.accountId,
        (visitorSid) => {
          this.ctmVisitorSid = visitorSid;
        },
      );
    }

    // Track widget initialization
    this.gtmTracker.trackWidgetInit({
      token: this.getAttribute("token"),
      sessionId: sessionId,
    });

    // Initialize WebSocket Manager
    if (!this.wsManager) {
      this.wsManager = new WebSocketManager(
        {
          maxReconnectAttempts: this.config.maxReconnectAttempts,
          reconnectDelayMs: this.config.reconnectDelayMs,
        },
        this.logger,
      );
      this.setupWebSocketListeners();
    }

    // Initialize Message Handler
    if (!this.messageHandler) {
      this.messageHandler = new MessageHandler(
        {
          onScrollToBottom: () => this.layout.scrollToBottom(),
          onUpdateActiveStatus: () => this.updateActiveStatus(),
          onDispatchEvent: (event, detail) =>
            this.dispatchCustomEvent(event, detail),
          onRenderMarkdown: (content) => this.renderMarkdownContent(content),
          onPlaySound: () => this.playNewMessageSound(),
          onFormLoad: (formId, data) =>
            this.formManager.registerForm(formId, data),
          onAdjustWindowHeight: () => this.layout.adjustWindowHeight(),
          onUpdateUIBasedOnState: () => this.layout.updateUIBasedOnState(),
          onSanitizeHTML: (html) => this.sanitizeHTML(html),
          onFetchFreshMediaUrl: (sid) => this.fetchFreshMediaUrl(sid),
        },
        this.logger,
      );
    }

    // Initialize Form Manager
    if (!this.formManager.messageHandler) {
      this.formManager.initialize({
        messageHandler: this.messageHandler,
        onFormSubmit: (formData) => this.handleFormSubmit(formData),
        onFormResize: () => this.layout.adjustWindowHeight(),
        onSendMessage: async (message) => {
          const connected = await this.ensureWebSocketConnected();
          if (connected) {
            this.sendMessageToServer(message);
          }
        },
        onDispatchEvent: (event, detail) =>
          this.dispatchCustomEvent(event, detail),
      });
    }

    if (!this.config.host) {
      if (this.messageHandler) {
        this.messageHandler.addMessage({
          type: "system",
          content: "Error: Widget is not configured correctly (missing host).",
        });
      }
      return; // Stop if host is missing
    }

    this.updateBaseUrlsFromHost();

    //console.log("CTM Chat Widget: Initializing...");

    if (!this.config.baseApiUrl || !this.config.baseWsUrl) {
      this.messageHandler?.addMessage({
        type: "system",
        content: "Error: Cannot determine backend location.",
      });
      return; // Stop initialization if URLs can't be derived
    }
    this.bindElements();

    if (this.config.chatId) {
      await this.fetchConfig();
    } else {
      this.messageHandler?.addMessage({
        type: "system",
        content: "Error: Widget is not configured correctly (missing ID).",
      });
    }

    if (!this._ownsPageWidgetSlot) {
      return;
    }

    // applyInitialConfig is called within fetchConfig or after the error message
    this.addEventListeners();
    this.layout.adjustWindowHeight(); // Adjust height initially

    this.initAudio(); // Initialize audio context and load sound

    // Set up reactive state subscriptions for layout
    this.layout.listenForStateChanges(this.stateManager);

    // Engage message timer is now set in showWidget() after schedule check
    // to prevent showing engage message before widget is visible

    this.stateManager.set("ready", true);
  }

  /**
   * Dispatches custom events from the chat widget
   * @param {string} eventName - Name of the event to dispatch
   * @param {Object} detail - Event detail data
   */
  dispatchCustomEvent(eventName, userDetail = {}) {
    const previewMode = this.isPreviewMode();
    if (!previewMode && !this.startTracking) {
      this.startTracking = true;
      this.initTracking();
    }
    const detail = {
      ...userDetail,
      timestamp: new Date().toISOString(),
      sessionId: this.stateManager.get("sessionId"),
      chatId: this.config.chatId,
      isAIEnabled: this.stateManager.get("aiEnabled"),
      connectionStatus: this.stateManager.get("connectionStatus"),
    };
    const bubbles = !previewMode;
    const composed = !previewMode;
    const event = new CustomEvent(eventName, { detail, bubbles, composed });
    this.dispatchEvent(event);
    if (!previewMode) {
      // Also dispatch CTM events to the global window for customer integrations.
      detail.target = this;
      window.dispatchEvent(
        new CustomEvent(`ctm-chat-${eventName}`, {
          detail,
          bubbles,
          composed,
        }),
      );
    }
  }
  initTracking() {
    const src = "https://cdn.lgrckt-in.com/LogRocket.min.js";
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      window.LogRocket && window.LogRocket.init("ctm-mwsas/chataiv2");
    };
    this.appendChild(script);
  }

  ensureMobileViewport() {
    if (!isMobile()) return;

    // Check if viewport meta tag exists
    let viewport = document.querySelector('meta[name="viewport"]');

    if (!viewport) {
      // Create viewport meta tag for proper mobile scaling
      viewport = document.createElement("meta");
      viewport.name = "viewport";
      viewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
      document.head.appendChild(viewport);
      //console.log('CTM Chat Widget: Added viewport meta tag for mobile');
    } else {
      // Ensure viewport includes necessary properties
      const content = viewport.content;
      if (!content.includes("width=device-width")) {
        viewport.content = content + ", width=device-width";
      }
      if (!content.includes("initial-scale=1")) {
        viewport.content = viewport.content + ", initial-scale=1.0";
      }
    }
  }

  /* ===========================================================
   *  Cross‑platform keyboard detection helpers
   * ===========================================================
   *
   *  Strategy
   *  --------
   *  • Modern iOS (13+) exposes window.visualViewport.
   *    When the keyboard slides up, visualViewport.height drops
   *    (and offsetTop may jump on iOS 16+).
   *  • We watch 'resize' AND 'scroll' on visualViewport.
   *  • A >120 px delta reliably means "keyboard visible".
   *  • Fallback: focus/blur on the input when visualViewport
   *    is unavailable (iOS <13 / legacy Android web‑views).
   */

  setupKeyboardDetection() {
    if (!isMobile()) return; // desktop → ignore
    if (this._viewportHandler) return; // already wired

    const THRESHOLD = 120; // px‑heuristic
    const handler = () => {
      // height difference = area taken by keyboard / browser UI
      const { height, offsetTop } = window.visualViewport;
      const diff = window.innerHeight - (height + offsetTop);
      const isOpen = diff > THRESHOLD;
      // console.log("keyboard detection:", {isOpen, height, offsetTop, diff});
      if (isOpen !== this.stateManager.get("keyboardOpen")) {
        this.stateManager.set("keyboardOpen", isOpen);
        if (isOpen) {
          this.adjustForMobileKeyboard(); // your existing helper
        } else {
          this.resetMobileKeyboardAdjustment();
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handler);
      window.visualViewport.addEventListener("scroll", handler);
      this._viewportHandler = handler;
    } else {
      // very old iOS – fall back to focus/blur
      this.inputEl?.addEventListener("focus", () => {
        this.stateManager.set("keyboardOpen", true);
        this.adjustForMobileKeyboard();
      });
      this.inputEl?.addEventListener("blur", () => {
        this.stateManager.set("keyboardOpen", false);
        // small delay so blur fired during field‑switch doesn't flicker
        setTimeout(() => this.resetMobileKeyboardAdjustment(), 150);
      });
    }
  }

  teardownKeyboardDetection() {
    if (this._viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener(
        "resize",
        this._viewportHandler,
      );
      window.visualViewport.removeEventListener(
        "scroll",
        this._viewportHandler,
      );
    }
    this._viewportHandler = null;
  }

  async loadMarkdownLibrary() {
    try {
      // Check if libraries are already available globally
      const markedAvailable = window.marked;
      const domPurifyAvailable = window.DOMPurify;

      // Load marked if not available
      if (!markedAvailable) {
        const markedScript = document.createElement("script");
        markedScript.src =
          "https://cdn.jsdelivr.net/npm/marked@15.0.12/lib/marked.umd.min.js";
        markedScript.async = true;

        await new Promise((resolve, reject) => {
          markedScript.onload = () => {
            this.marked = window.marked;
            //console.log("CTM Chat Widget: Marked library loaded from CDN");
            resolve();
          };
          markedScript.onerror = () => {
            this.logger.error("Failed to load marked library");
            reject();
          };
          document.head.appendChild(markedScript);
        });
      } else {
        this.marked = window.marked;
        //console.log("CTM Chat Widget: Marked library already loaded globally");
      }

      // Load DOMPurify if not available
      if (!domPurifyAvailable) {
        const purifyScript = document.createElement("script");
        purifyScript.src =
          "https://cdn.jsdelivr.net/npm/dompurify@3.0.6/dist/purify.min.js";
        purifyScript.async = true;

        await new Promise((resolve, reject) => {
          purifyScript.onload = () => {
            this.DOMPurify = window.DOMPurify;
            //console.log("CTM Chat Widget: DOMPurify library loaded from CDN");
            resolve();
          };
          purifyScript.onerror = () => {
            this.logger.error("Failed to load DOMPurify library");
            reject();
          };
          document.head.appendChild(purifyScript);
        });
      } else {
        this.DOMPurify = window.DOMPurify;
        // console.log("CTM Chat Widget: DOMPurify library already loaded globally");
      }

      // If we have messages already loaded, re-render them with markdown support
      const messages = this.messageHandler?.getMessages() || [];
      if (messages.length > 0) {
        // console.log("CTM Chat Widget: Re-rendering messages with markdown support");
        this.messageHandler?.renderMessages();
      }
    } catch (error) {
      this.logger.error(
        "Error loading markdown/sanitization libraries:",
        error,
      );
      // Continue without markdown support
    }
  }

  /**
   * Sanitize HTML content to prevent XSS attacks
   * @param {string} html - The HTML content to sanitize
   * @returns {string} - The sanitized HTML or plain text if DOMPurify not available
   */
  sanitizeHTML(html) {
    if (this.DOMPurify) {
      return this.DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "code",
          "pre",
          "blockquote",
          "ul",
          "ol",
          "li",
          "a",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "table",
          "thead",
          "tbody",
          "tr",
          "th",
          "td",
          "hr",
          "del",
          "ins",
        ],
        ALLOWED_ATTR: ["href", "title", "target", "rel"],
        ALLOW_DATA_ATTR: false,
        ADD_TAGS: [],
        ADD_ATTR: ["target"], // Allow target for links
        FORCE_BODY: true,
        SANITIZE_DOM: true,
      });
    }
    // If DOMPurify not available, return empty string (caller should use textContent)
    return "";
  }

  updateBaseUrlsFromHost() {
    // Delegate to config manager
    this.configManager.updateBaseUrls();
    this.config = this.configManager.get(); // Update local reference
  }

  // --- Initialization ---

  bindElements() {
    this.bubbleEl = this.shadowRoot.getElementById("chat-toggle");
    this.chatWindowEl = this.shadowRoot.getElementById("chat-window");
    this.messagesContainerEl =
      this.shadowRoot.getElementById("messages-container");
    this.inputEl = this.shadowRoot.getElementById("chat-input");
    this.sendBtnEl = this.shadowRoot.getElementById("send-button");
    this.suggestionsContainerEl =
      this.shadowRoot.getElementById("suggestion-buttons");
    this.engageMessageEl = this.shadowRoot.getElementById("engage-message");
    this.engageIconEl = this.shadowRoot.getElementById("engage-icon");
    this.engageTextEl = this.shadowRoot.getElementById("engage-text");
    this.engageCloseEl = this.shadowRoot.getElementById("engage-close");

    // Set messages container on MessageHandler
    if (this.messageHandler) {
      this.messageHandler.setMessagesContainer(this.messagesContainerEl);
    }
    this.headerTitleEl = this.shadowRoot.getElementById("header-title");
    this.header = this.shadowRoot.querySelector(".header");

    this.optionsMenuEl = this.shadowRoot.getElementById("options-menu");
    this.optionsToggleBtn =
      this.shadowRoot.getElementById("options-toggle-btn");
    this.backBtnEl = this.shadowRoot.getElementById("back-btn");
    this.headerCloseBtnEl = this.shadowRoot.getElementById("header-close-btn");

    this.endChatBtn = this.shadowRoot.getElementById("end-chat-btn");
    this.endChatItemEl = this.shadowRoot.getElementById("end-chat-item");
    this.copyEvalBtn = this.shadowRoot.getElementById("copy-eval-btn");
    this.copyEvalItemEl = this.shadowRoot.getElementById("copy-eval-item");
    this.mobileCloseBtn = this.shadowRoot.getElementById("mobile-close-btn");

    // Only show "Copy as Eval" on CTM domains
    if (this.copyEvalItemEl) {
      const host = window.location.hostname;
      const isCTM = host === "calltrackingmetrics.com" || host.endsWith(".calltrackingmetrics.com")
        || host === "ctmdev.us" || host.endsWith(".ctmdev.us");
      this.copyEvalItemEl.style.display = isCTM ? "" : "none";
    }

    this.typingIndicatorEl = this.shadowRoot.getElementById("typing-indicator");
    this.aiStatusIndicatorEl = this.shadowRoot.getElementById(
      "ai-status-indicator",
    );

    this.connectionStatusEl =
      this.shadowRoot.getElementById("connection-status"); // Bind the new element

    // File upload elements
    this.fileInputEl = this.shadowRoot.getElementById("file-input");
    this.attachBtnEl = this.shadowRoot.getElementById("attach-button");

    // Ensure input visibility/button state is correct before async state updates land.
    this.layout.updateInputState();
  }

  applyInitialConfig() {
    this.updateToken();

    if (this.config.aiEnabled !== undefined) {
      this.stateManager.set("aiEnabled", this.config.aiEnabled);
    }

    //  console.log("apply initial config.singleButtonDefault:", this.config.singleButtonDefault);

    if (this.config.singleButtonDefault !== undefined) {
      this.stateManager.set(
        "singleButtonDefault",
        this.config.singleButtonDefault,
      );
    }

    if (this.headerTitleEl)
      this.headerTitleEl.textContent = this.config.headerText;

    this.setSuggestions(this.config.suggestions);
    this.ensureWelcomeMessage();
    this.layout.update();

    // Check if attachments should be enabled by default
    if (this.config.attachmentsEnabled) {
      this.enableAttachments();
    }

    // Apply schedule visibility
    this.applyScheduleVisibility();
  }

  addEventListeners() {
    this.toggleChat = this.toggleChat.bind(this); // Bind context for removeEventListener
    this.handleSendMessage = this.handleSendMessage.bind(this);
    this.handleInputKeypress = this.handleInputKeypress.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.toggleOptionsMenu = this.toggleOptionsMenu.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleResize = this.handleResize.bind(this); // Bind resize handler
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this); // Bind suggestion handler

    this.handleEndChat = this.handleEndChat.bind(this);
    this.handleBackToMenu = this.handleBackToMenu.bind(this);
    this.handleHeaderClose = this.handleHeaderClose.bind(this);
    this.handleAttachClick = this.handleAttachClick.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.handleEngageClick = this.handleEngageClick.bind(this);
    this.handleEngageClose = this.handleEngageClose.bind(this);
    this.handleDebugClick = this.handleDebugClick.bind(this);

    // Secret debug click handler on header title (5 rapid clicks to copy config)
    this.headerTitleEl?.addEventListener("click", this.handleDebugClick);

    // Touch-friendly click handling
    this.header.addEventListener("click", this.toggleChat);
    this.header.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.header.addEventListener("touchend", this.handleTouchEnd);
    this.bubbleEl?.addEventListener("click", this.toggleChat);
    this.bubbleEl?.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.bubbleEl?.addEventListener("touchend", this.handleTouchEnd);

    // Engagement message click handlers
    this.engageMessageEl?.addEventListener("click", this.handleEngageClick);
    this.engageCloseEl?.addEventListener("click", this.handleEngageClose);

    this.sendBtnEl?.addEventListener("click", this.handleSendMessage);
    this.inputEl?.addEventListener("keypress", this.handleInputKeypress);
    this.inputEl?.addEventListener("input", this.handleInput); // Check input for send button state

    // Prevent keyboard from pushing up the chat window on mobile
    if (isMobile()) {
      this.inputEl?.addEventListener("focus", () => {
        // Mark that we're expecting keyboard
        this.keyboardExpected = true;

        // Try immediate adjustment
        this.adjustForMobileKeyboard();

        // Also try with delay to ensure keyboard is shown
        setTimeout(() => {
          this.adjustForMobileKeyboard();
        }, 300);

        // Fallback for older browsers without visualViewport
        if (!window.visualViewport) {
          // Use a more aggressive approach for older browsers
          setTimeout(() => {
            if (this.chatWindowEl && this.inputEl) {
              // Scroll the input into view
              const inputEl = this.shadowRoot.getElementById("chat-input");
              if (inputEl) {
                inputEl.scrollIntoView({ behavior: "smooth", block: "end" });
              }
            }
          }, 500);
        }
      });

      this.inputEl?.addEventListener("blur", () => {
        this.keyboardExpected = false;
        // Small delay to avoid premature reset during field switching
        setTimeout(() => {
          if (!this.keyboardExpected) {
            this.resetMobileKeyboardAdjustment();
          }
        }, 100);
      });
    }

    this.optionsToggleBtn?.addEventListener("click", this.toggleOptionsMenu);
    this.backBtnEl?.addEventListener("click", this.handleBackToMenu);
    this.headerCloseBtnEl?.addEventListener("click", this.handleHeaderClose);
    this.mobileCloseBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleChat(e);
    });

    this.endChatBtn?.addEventListener("click", this.handleEndChat);
    this.copyEvalBtn?.addEventListener("click", () => this.copyAsEval());

    // File upload event listeners
    this.attachBtnEl?.addEventListener("click", this.handleAttachClick);
    this.fileInputEl?.addEventListener("change", this.handleFileSelect);

    // Drag and drop event listeners (desktop only)
    if (!isMobile()) {
      this.messagesContainerEl?.addEventListener(
        "dragover",
        this.handleDragOver,
      );
      this.messagesContainerEl?.addEventListener("drop", this.handleDrop);
    }

    // Close options menu if clicking outside
    document.addEventListener("click", this.handleOutsideClick, true); // Use capture phase

    // Add resize listener with debouncing
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("orientationchange", this.handleResize);

    // Handle page visibility changes (for better battery life on mobile)
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  async fetchConfig() {
    try {
      const result = this.isPreviewMode()
        ? await this.configManager.fetch({ cache: "no-store" })
        : await this.configManager.fetch();
      if (!this._ownsPageWidgetSlot) {
        return;
      }

      // Handle schedule visibility
      if (result.scheduleVisibility) {
        const currentScheduleVisibility =
          this.stateManager.get("scheduleVisibility") || {};
        this.stateManager.set("scheduleVisibility", {
          ...currentScheduleVisibility,
          ...result.scheduleVisibility,
        });

        // Apply visibility immediately
        const scheduleVisibility = this.stateManager.get("scheduleVisibility");
        if (!scheduleVisibility?.isVisible) {
          this.hideWidget();
        }

        // Set up next transition check if there's a schedule
        if (
          scheduleVisibility?.hasSchedule &&
          scheduleVisibility?.nextTransition
        ) {
          this.scheduleNextVisibilityCheck();
        }
      }

      if (result.config?.agentAvailability) {
        this.configManager.setAgentAvailability(result.config.agentAvailability);
        this.stateManager.setAgentAvailability(result.config.agentAvailability);
      }

      // Merge configuration
      this.configManager.merge(result.config);
      this.config = this.configManager.get(); // Update local reference

      this.applyInitialConfig(); // Apply merged config to UI

      this.prepareConnectionAfterConfig();
    } catch (error) {
      this.messageHandler?.addMessage({
        type: "system",
        content: error.message,
      });
      throw error;
    }
  }

  prepareConnectionAfterConfig() {
    if (!this.config.baseWsUrl || !this.config.chatId) {
      this.stateManager.set("lazyConnectionReady", false);
      return;
    }

    const sessionCookieName = this.getSessionCookieName();
    const sessionId = getSessionIdFromCookie(sessionCookieName);
    this.stateManager.set("sessionId", sessionId);

    // Reconnect immediately only when a CTM chat was in progress (marked by
    // markActiveCall, cleared on chatEnded). A bare session cookie stays on
    // the lazy path so page loads don't materialize dormant sessions, but an
    // active conversation must reconnect to receive queued agent replies —
    // otherwise the chat idle-ends server-side while the visitor sees nothing.
    const hasActiveChat =
      getCookie(`${sessionCookieName}_active`) === "true";
    if (sessionId && hasActiveChat) {
      this._validatingActiveCallReconnect = true;
      this.stateManager.set("lazyConnectionReady", false);
      if (
        !this.wsManager?.isConnected() &&
        this.stateManager.get("connectionStatus") !== "connecting"
      ) {
        this.connectWebSocket();
      }
      return;
    }

    this.stateManager.set("lazyConnectionReady", true);
    this.stateManager.set("initialConnectionAttempted", false);
    this.layout.updateUIBasedOnState();
    this.layout.updateInputState();
  }

  initAudio() {
    try {
      // Initialize audio and pre-load sounds
      if (this.audioManager && !this.audioManager.hasSound("newMessage")) {
        this.audioManager.loadSound(
          "newMessage",
          `${this.config.baseApiUrl}/newMessage.mp3`,
        );
      }
    } catch (e) {
      this.logger.error("Error initializing AudioContext:", e);
    }
  }

  // Setup WebSocket event listeners
  setupWebSocketListeners() {
    this.wsManager.on("open", ({ wasReconnecting }) => {
      const messages = this.messageHandler?.getMessages() || [];
      const hadTemporaryMessages = messages.some((m) => m.temporary);
      this.messageHandler?.filterMessages((m) => !m.temporary);

      if (hadTemporaryMessages && wasReconnecting) {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Reconnected!",
          temporary: true,
        });

        setTimeout(() => {
          this.messageHandler?.filterMessages(
            (m) => m.content !== "Reconnected!",
          );
        }, 2000);
      }

      this.updateActiveStatus();

      if (this.stateManager.get("isOpen")) {
        this.sendMessageToServer({ type: "widgetState", state: "open" }, false);
      }

      this.stateManager.set("initialConnectionAttempted", true);
      this.updateHeartbeatState();
    });

    this.wsManager.on("message", (message) => {
      this.handleWebSocketMessage(message);
    });

    this.wsManager.on("close", ({ code, reason, wasClean }) => {
      if (code === 1008) {
        this.messageHandler?.filterMessages((m) => !m.temporary);
        this.endChat();
      }
      this.updateHeartbeatState();
      this.layout.updateUIBasedOnState();
    });

    this.wsManager.on("error", ({ message }) => {
      this.logger.error(`WebSocket Error: ${message}`);
    });

    this.wsManager.on("statusChange", ({ status }) => {
      // Don't allow WebSocket status changes to override the 'ended' state
      // (onclose fires asynchronously and would overwrite 'ended' with 'closed')
      if (this.stateManager.get("connectionStatus") === "ended") {
        return;
      }
      this.stateManager.set("connectionStatus", status); // This will trigger the layout update automatically

      // Track connection status changes
      this.gtmTracker.trackConnectionStatus(status);

      // Clear typing/AI status indicators when connection is lost
      if (
        status === "disconnected" ||
        status === "closed" ||
        status === "error" ||
        status === "reconnecting"
      ) {
        this.messageHandler?.showTypingIndicator(false);
        this.layout.updateAIStatusIndicator("idle");
        clearTimeout(this._aiStatusTimeout);
        this._aiStatusTimeout = null;
      }

      if (status === "reconnecting") {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Connection lost. Reconnecting...",
          temporary: true,
        });
      } else if (status === "error") {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Connection failed permanently.",
        });
      }
      this.updateHeartbeatState();
    });
  }

  // --- WebSocket Communication (Direct Only) ---
  async resolveCtmVisitorSid() {
    if (this.isPreviewMode()) {
      this.ctmVisitorSid = null;
      return null;
    }

    if (!hasCtmMarketingConsent()) {
      this.ctmVisitorSid = null;
      return null;
    }

    const activeVisitorSid = getActiveCtmVisitorSid(this.config.accountId);
    if (activeVisitorSid) {
      this.ctmVisitorSid = activeVisitorSid;
    } else {
      this.ctmVisitorSid = normalizeCtmVisitorSid(
        this.ctmVisitorSid,
        this.config.accountId,
      );
    }

    if (!this.ctmVisitorSid) {
      this.ctmVisitorSid = await waitForActiveCtmVisitorSid(
        this.config.accountId,
      );
    }

    if (!this.ctmVisitorSid && this.config.trackingHost) {
      const fallbackVisitorSid = await getOrCreateCtmVisitorSid(
        this.config.accountId,
        this.config.trackingHost,
      );
      this.ctmVisitorSid =
        getActiveCtmVisitorSid(this.config.accountId) ||
        normalizeCtmVisitorSid(
          this.ctmVisitorSid,
          this.config.accountId,
        ) ||
        fallbackVisitorSid;
    }

    return this.ctmVisitorSid;
  }

  async connectWebSocket() {
    if (!this.wsManager || !this.config.baseWsUrl || !this.config.chatId) {
      return;
    }

    // Reset session assignment flag for new connection (security measure)
    this.stateManager.set("sessionAssignmentReceived", false);
    this.stateManager.set("initialConnectionAttempted", true);
    this.stateManager.set("lazyConnectionReady", false);

    // Try to get existing session from cookie for reconnection
    const sessionId = getSessionIdFromCookie(this.getSessionCookieName());
    this.stateManager.set("sessionId", sessionId);
    const visitorSid = await this.resolveCtmVisitorSid();

    return this.wsManager.connect({
      baseWsUrl: this.config.baseWsUrl,
      chatId: this.config.chatId,
      accountId: this.config.accountId,
      region: this.token.r,
      sessionId: this.stateManager.get("sessionId"),
      visitorSid,
      tabId: this.tabId,
    });
  }

  hasMaterializedSession() {
    return !!this.stateManager.get("sessionId") || !!this.wsManager?.isConnected();
  }

  async ensureWebSocketConnected({ showError = true } = {}) {
    if (this.wsManager?.isConnected()) {
      return true;
    }

    if (!this.wsManager || !this.config.baseWsUrl || !this.config.chatId) {
      if (showError) {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Cannot start chat. Widget is not configured correctly.",
        });
      }
      return false;
    }

    try {
      await this.connectWebSocket();
      return this.wsManager?.isConnected() || false;
    } catch (error) {
      this.logger.error("Failed to connect WebSocket:", error);
      if (showError) {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Cannot connect. Please try again.",
        });
      }
      return false;
    }
  }

  sendMessageToServer(messageObject, clear = true) {
    ensureClientTurnId(messageObject);
    if (this.wsManager && this.wsManager.isConnected()) {
      const sent = this.wsManager.send(messageObject);
      if (sent && this.isActiveCallActivityMessage(messageObject)) {
        this.refreshActiveCallMarker();
      }
      if (sent && clear) {
        const aiEnabled = this.stateManager.get("aiEnabled");
        const hasActiveCall = !!this.stateManager.get("callId");
        const waitingForAgent = !!this.stateManager.get("waitingForAgent");
        const isSuggestion = !!messageObject?.isSuggestion;
        const isActivation =
          messageObject?.type === "activateAI" ||
          messageObject?.type === "activateHumanAgent";
        const shouldClearSuggestions =
          aiEnabled ||
          hasActiveCall ||
          waitingForAgent ||
          isSuggestion ||
          isActivation;
        if (shouldClearSuggestions) {
          this.setSuggestions([]);
        }
      }
      return sent;
    } else {
      //console.warn("WebSocket: Cannot send message, not open.");
      if (clear) {
        this.messageHandler?.addMessage({
          type: "system",
          content: "Cannot send message. Not connected.",
        });
      }
      return false;
    }
  }

  /**
   * Return the public client-tool configuration, including legacy snake_case.
   * @returns {Object} Client-tool configuration.
   */
  getClientToolConfig() {
    return this.config?.clientTools || this.config?.client_tools || {};
  }

  /**
   * Capture page context and expose only links allowed by navigation policy.
   * @returns {Object} Sanitized page context.
   */
  capturePageForAI() {
    const context = capturePageContext({
      document,
      currentUrl: window.location.href,
    });
    if (!context.success) {
      return context;
    }

    const navigationConfig = this.getClientToolConfig().navigation || {};
    if (navigationConfig.enabled) {
      const allowedUrls =
        navigationConfig.allowedUrls || navigationConfig.allowed_urls || [];
      const allowlistEnabled =
        navigationConfig.allowlistEnabled ||
        navigationConfig.allowlist_enabled ||
        false;
      context.links = filterLinksByNavigationPolicy(
        context.links,
        context.url,
        allowedUrls,
        allowlistEnabled,
      );
    }

    return context;
  }

  /**
   * Execute a browser-owned tool request and return its result to the Worker.
   * @param {Object} payload Client tool request payload.
   * @returns {Promise<void>}
   */
  async handleClientToolRequest(payload) {
    const requestId = payload?.requestId;
    const toolName = payload?.name;
    const args = payload?.args || {};
    let result;
    let navigationUrl = null;
    const clientToolConfig = this.getClientToolConfig();

    if (!requestId || !toolName) {
      this.logger.warn("Ignoring malformed client tool request", payload);
      return;
    }

    try {
      if (toolName === "Page") {
        if (clientToolConfig.pageAccess?.enabled === false) {
          result = {
            success: false,
            error: "Page Access is disabled for this widget.",
          };
        } else {
          result = this.capturePageForAI();
        }
      } else if (toolName === "Navigation") {
        const navigationConfig = clientToolConfig.navigation || {};
        if (navigationConfig.enabled === false) {
          result = {
            success: false,
            error: "Page Navigation is disabled for this widget.",
          };
        } else {
          const pageContext = capturePageContext({
            document,
            currentUrl: window.location.href,
          });
          if (!pageContext.success) {
            result = pageContext;
          } else {
            result = validateNavigationTarget({
              requestedUrl: args.url,
              currentUrl: pageContext.url,
              links: pageContext.links,
              allowedUrls:
                navigationConfig.allowedUrls ||
                navigationConfig.allowed_urls ||
                [],
              allowlistEnabled:
                navigationConfig.allowlistEnabled ||
                navigationConfig.allowlist_enabled ||
                false,
            });
            if (result.success) {
              navigationUrl = result.url;
            }
          }
        }
      } else {
        result = {
          success: false,
          error: `Unknown client tool: ${toolName}`,
        };
      }
    } catch (error) {
      this.logger.error(`Client tool ${toolName} failed`, error);
      result = {
        success: false,
        error: "The browser could not complete the requested action.",
      };
    }

    const sent = this.sendMessageToServer(
      {
        type: "clientToolResult",
        requestId,
        result,
      },
      false,
    );

    if (sent && navigationUrl) {
      setTimeout(() => {
        this.navigateToPage(navigationUrl);
      }, CLIENT_TOOL_NAVIGATION_DELAY_MS);
    }
  }

  /**
   * Navigate the host page, or emit a preview-only event in the editor.
   * @param {string} url Validated destination URL.
   */
  navigateToPage(url) {
    if (this.isPreviewMode()) {
      this.dispatchCustomEvent("preview-navigation", { url });
      return;
    }
    window.location.assign(url);
  }

  handleWebSocketMessage(data) {
    const payload = data.payload?.payload ?? data.payload ?? data; // Handle potential nesting variations
    //console.log('CTM Chat Widget: Handling WS Message:', data.type, payload);
    //console.log('CTM Chat Widget: Full message data:', JSON.stringify(data));

    switch (data.type) {
      case "clientToolRequest":
        this.handleClientToolRequest(payload);
        break;
      case "sessionAssigned":
        // Handle session ID assignment during initial handshake
        const sessionAssignmentReceived = this.stateManager.get(
          "sessionAssignmentReceived",
        );
        if (data.sessionId && !sessionAssignmentReceived) {
          //console.log(`WebSocket: Received initial session ID from server: ${data.sessionId}`);
          setSessionIdCookie(this.getSessionCookieName(), data.sessionId);
          this.stateManager.set("sessionId", data.sessionId);
          this.stateManager.set("sessionAssignmentReceived", true); // Mark as received to prevent session fixation
          this.stateManager.set("lazyConnectionReady", false);

          // Track session start
          this.gtmTracker.trackSessionStart(data.sessionId, false);

          // Store the actual durable object ID if provided
          if (data.durableObjectId) {
            //  console.log("data.durableObjectId:", data.durableObjectId);
            this.stateManager.set("durableObjectId", data.durableObjectId);
            //console.log(`WebSocket: Received Durable Object ID: ${data.durableObjectId}`);
          }

          // Update MessageHandler with session info
          const sessionId = this.stateManager.get("sessionId");
          const durableObjectId = this.stateManager.get("durableObjectId");
          this.messageHandler?.setSessionInfo(sessionId, durableObjectId);
        }

        if (this._validatingActiveCallReconnect) {
          this._validatingActiveCallReconnect = false;
          if (!payload.callId) {
            deleteCookie(`${this.getSessionCookieName()}_active`);
          }
        }

        // Process AI enabled state
        if (typeof payload.aiEnabled !== "undefined") {
          this.stateManager.set("aiEnabled", payload.aiEnabled);
          this.layout.updateInputState();
        }

        if (typeof payload.singleButtonDefault !== "undefined") {
          //  console.log("apply: singleButtonDefault:", payload.singleButtonDefault);
          this.stateManager.set(
            "singleButtonDefault",
            payload.singleButtonDefault,
          );
          // console.log("WS: Received singleButtonDefault:", payload.singleButtonDefault);
          this.layout.updateUIBasedOnState();
        }

        // Process call ID
        if (payload.callId) {
          this.markActiveCall(payload.callId);
          // console.log("WS: Received callId:", payload.callId);
          // Update UI to hide suggestions when callId is received
          this.layout.updateUIBasedOnState();
          this.updateActiveStatus(); // Update widget visibility when call is established
          this.updateHeartbeatState();
        }
        break;
      // --- Removed connectionState handling (done in WS callbacks now) ---
      // case 'connectionState': ...
      case "widgetState":
        // console.log("WS: widgetState:", payload);
        const isOpen = this.stateManager.get("isOpen");
        if (payload.state === "open" && !isOpen) {
          this.toggleChat();
        }
        break;
      case "widgetConfig":
        // console.log("WS: widgetConfig received:", payload);
        if (payload) {
          // Extract agent availability data if present
          if (payload.agentAvailability) {
            this.configManager.setAgentAvailability(payload.agentAvailability);
            this.stateManager.setAgentAvailability(payload.agentAvailability);
          }

          // Merge received config
          this.configManager.merge(payload);
          this.config = this.configManager.get(); // Update local reference

          // Apply the configuration to update UI
          this.applyInitialConfig();
          this.layout.update();

          // Call the refresh handler if waiting for config update
          if (this._configUpdateHandler) {
            this._configUpdateHandler(data);
            this._configUpdateHandler = null;
          }
        }
        break;
      case "agentAvailabilityChanged":
        // Handle real-time agent availability updates from server
        // console.log("WS: agentAvailabilityChanged received:", payload);
        if (payload) {
          // Update availability in both managers
          this.configManager.setAgentAvailability(payload);
          this.stateManager.setAgentAvailability(payload);

          // Re-evaluate suggestions based on new availability
          if (this.config.suggestions) {
            this.setSuggestions(this.config.suggestions);
          }

          // Re-evaluate engage messages - layout.update() handles this
          this.layout.update();
        }
        break;
      case "history":
        // console.log("WS: received history: ", payload?.messages);
        const receivedHistory = payload?.messages || [];

        // Only clear messages if we're receiving actual history
        // This prevents clearing messages on empty history during reconnection
        if (receivedHistory.length > 0) {
          // Clear existing non-system messages before loading history
          this.messageHandler?.filterMessages(
            (m) => m.type === "system" && m.content.startsWith("Error:"),
          );

          // Transform history messages to client format
          const transformedHistory = receivedHistory.map((msg) => {
            // Determine the type based on role or other properties
            let type = "user";
            if (msg.role === "assistant" || msg.twilioMessage) {
              type =
                msg.author &&
                (msg.author.includes("bot@") || msg.author.includes("-bot@"))
                  ? "ai"
                  : "agent";
            } else if (msg.role === "user") {
              type = "user";
            } else if (msg.type) {
              type = msg.type;
            }
            const file = this.decorateFileMetadata(msg.file);

            return {
              type: type,
              content: msg.content,
              role: msg.role, // Preserve role for markdown parsing
              author: msg.author,
              timestamp: msg.timestamp,
              media: msg.media,
              file,
              twilioMessage: msg.twilioMessage,
              ...(msg.welcome && { welcome: msg.welcome }),
            };
          });

          // Add transformed history messages
          transformedHistory.forEach((msg) =>
            this.messageHandler?.addMessage(msg, { silent: true }),
          );
        } else if ((this.messageHandler?.getMessages() || []).length === 0) {
          // No existing messages and no history received - likely a fresh start or failed reconnection
          // console.log("WS: Empty history received, no existing messages");
        }

        // If there's a pending resume message, add and send it now that history is loaded
        if (this._pendingResumeMessage) {
          const messageObject =
            typeof this._pendingResumeMessage === "string"
              ? ensureClientTurnId({
                  type: "user",
                  content: this._pendingResumeMessage,
                  timestamp: Date.now(),
                })
              : this._pendingResumeMessage;
          this._pendingResumeMessage = null;
          this.messageHandler?.addMessage(messageObject);
          this.sendMessageToServer(messageObject);
        }

        this.messageHandler?.renderMessages(); // Full render after history load
        this.updateActiveStatus(); // Update widget visibility after loading history

        // Check if we have any meaningful conversation
        const messages = this.messageHandler?.getMessages() || [];
        const hasUserOrAIMessages = messages.some(
          (m) =>
            m.type === "user" ||
            m.role == "user" ||
            m.type === "ai" ||
            m.type === "agent" ||
            m.type === "assistant" ||
            m.role === "assistant",
        );
        const hasWelcomeMessage = messages.some((m) => m.welcome);

        // Show welcome only if: no history AND no welcome already shown
        if (!hasUserOrAIMessages && !hasWelcomeMessage) {
          // console.log("add the welcome message", hasUserOrAIMessages);
          this.messageHandler?.addMessage({
            type: "status",
            welcome: true,
            content: this.evaluateWelcomeMessage(),
          });
        }

        // Force show suggestions if we have no messages after reconnection
        const callId = this.stateManager.get("callId");
        if (!hasUserOrAIMessages && callId && this.suggestionsContainerEl) {
          //  console.log("WS: No messages after reconnection, showing suggestions despite callId");
          const visibleButtons =
            this.suggestionsContainerEl.querySelectorAll("button").length;
          if (visibleButtons > 0) {
            this.suggestionsContainerEl.style.display = "";
          }
        }

        this.layout.updateUIBasedOnState();
        this.layout.scrollToBottom();
        break;
      case "ai_status": {
        const aiStatus = payload?.status;
        this.layout.updateAIStatusIndicator(aiStatus);

        // Clear any existing timeout
        clearTimeout(this._aiStatusTimeout);
        this._aiStatusTimeout = null;

        // Set a timeout to auto-hide if status is active (not idle/completed/error)
        // This prevents the indicator from getting stuck if the server never sends idle
        if (
          aiStatus &&
          aiStatus !== "idle" &&
          aiStatus !== "completed" &&
          aiStatus !== "error"
        ) {
          this._aiStatusTimeout = setTimeout(() => {
            this.logger.debug("AI status timeout - auto-hiding indicator");
            this.layout.updateAIStatusIndicator("idle");
            this._aiStatusTimeout = null;
          }, 60000); // 60 second timeout
        }
        break;
      }
      case "ai_message_start":
        // console.log("WS: ai_message_start received with payload:", payload);
        this.messageHandler?.showTypingIndicator(false);
        this.layout.updateAIStatusIndicator("responding");
        this.messageHandler?.createNewAIMessageElement(payload?.id);
        // console.log("WS: Created new AI message element for ID:", payload?.id);
        break;
      case "ai_chunk":
        if (payload?.id && payload?.delta) {
          this.messageHandler?.updateAIMessage(payload.delta);
        }
        break;
      case "ai_message_end":
        if (payload?.id) {
          // Get the full content from the message element
          const messageEl = this.messagesContainerEl?.querySelector(
            `[data-message-id="${payload.id}"]`,
          );
          const textEl = messageEl?.querySelector(".text");
          const fullContent =
            textEl?.getAttribute("data-raw-content") ||
            textEl?.textContent ||
            "";

          // Finalize the AI message
          this.messageHandler?.finalizeAIMessage(fullContent, payload.id);
          this.layout.updateAIStatusIndicator("idle");
        }
        break;
      case "ai": // Complete AI message (might be non-streaming or final summary)
      case "agent": // Message from live agent
      case "system": // System message from backend/DO
      case "user": // User message relayed from Twilio
      case "message": // Generic message container (check payload type)
        this.messageHandler?.showTypingIndicator(false);
        this.layout.updateAIStatusIndicator("idle");
        // Determine actual message type if nested (e.g., payload.type)
        const messageToAdd = {
          type: payload?.type || data.type, // Use inner type if available
          content: payload?.content || payload, // Adjust based on structure
          role: payload?.role,
          id: payload?.id,
          // Add visibility attributes for system messages
          clientVisible: data.clientVisible,
          agentVisible: data.agentVisible,
          // Add other relevant fields like timestamp, name if available
          ...(payload?.name && {
            name: payload.name,
          }),
          ...(payload?.timestamp && {
            timestamp: payload.timestamp,
          }),
        };
        // Add only if content exists or has media
        if (messageToAdd.content || data.hasMedia) {
          // Check if this is a system message enabling attachments
          if (
            messageToAdd.type === "system" &&
            typeof messageToAdd.content === "string" &&
            messageToAdd.content.includes("User Attachments has been enabled")
          ) {
            this.enableAttachments();
          }

          // Add media information if present
          if (data.media && data.media.length > 0) {
            messageToAdd.media = data.media;
            messageToAdd.hasMedia = true;
          }

          this.messageHandler?.addMessage(messageToAdd);

          if (
            messageToAdd.type === "ai" ||
            messageToAdd.type === "assistant" ||
            messageToAdd.type === "agent" ||
            messageToAdd.type === "user"
          ) {
            this.refreshActiveCallMarker();
          }

          // Track incoming AI/agent messages
          if (messageToAdd.type === "ai" || messageToAdd.type === "assistant") {
            this.gtmTracker.trackMessageReceived("ai", {
              messageLength: messageToAdd.content?.length || 0,
            });
          } else if (messageToAdd.type === "agent") {
            this.gtmTracker.trackMessageReceived("agent", {
              messageLength: messageToAdd.content?.length || 0,
            });
          }

          // play sound only when the message is from an agent
          if (messageToAdd.type == "agent") {
            this.audioManager?.playSound("newMessage");
          }
        } else {
        }
        break;
      case "typing": {
        const mode = payload?.mode;
        const isTyping = !!payload?.isTyping;
        const participant = payload?.participant || "";
        const participantRole = payload?.participantRole || "";
        if (mode === "AGENT") {
          // Prefer explicit participantRole when available; fall back to identity heuristic
          const isHumanAgent =
            participantRole === "agent" ||
            (participant.startsWith("agent_") && !participant.includes("bot@"));
          if (!isHumanAgent) break;
          if (isTyping) {
            this.messageHandler?.showTypingIndicator(true);
            clearTimeout(this._typingHideTimer);
            this._typingHideTimer = setTimeout(() => {
              this.messageHandler?.showTypingIndicator(false);
              this._typingHideTimer = null;
            }, 4000); // auto-hide if no subsequent typing
          } else {
            this.messageHandler?.showTypingIndicator(false);
            clearTimeout(this._typingHideTimer);
            this._typingHideTimer = null;
          }
        }
        break;
      }
      case "callIdAssigned":
        if (payload.callId) {
          this.markActiveCall(payload.callId);
          // Update UI to hide suggestions when callId is assigned
          this.layout.updateUIBasedOnState();
          this.updateActiveStatus(); // Update widget visibility when call is assigned
          this.logger.debug(
            "[Widget] callIdAssigned:",
            payload.callId,
            "state:",
            this.stateManager.getSnapshot(),
          );
        }
        break;
      case "suggestions":
        this.setSuggestions(payload?.suggestions || []);
        break;
      case "updateHeader":
        if (this.headerTitleEl)
          this.headerTitleEl.textContent =
            payload?.text || this.config.headerText;
        break;

      case "error": // Explicit error message from backend
        //console.error("CTM Chat Widget: Error from WebSocket:", payload?.message);
        /*this.messageHandler?.addMessage({
        type: "system",
        content: `Error: ${payload?.message || "Unknown error from server."}`,
      });*/
        break;
      case "form":
        // inject the form... unless it's already here...

        break;
      case "aiStateChanged":
        this.logger.debug("[Widget] aiStateChanged payload:", payload);
        this.stateManager.set("aiEnabled", payload.aiEnabled);
        this.stateManager.set(
          "singleButtonDefault",
          payload.singleButtonDefault,
        );
        this.layout.updateInputState();
        if (payload.callId) {
          this.markActiveCall(payload.callId);
          this.updateActiveStatus(); // Update widget visibility when AI state changes with callId
        }
        this.logger.debug(
          "[Widget] state after aiStateChanged",
          this.stateManager.getSnapshot(),
        );
        break;
      case "chatEnded":
        this.handleChatEnded(payload);
        break;
      case "chatResumed":
        this.handleChatResumed(payload);
        break;
      case "agentConnected":
        this.gtmTracker.trackAgentHandoff("human");
        this.handleAgentConnected(payload);
        this.logger.debug(
          "[Widget] agentConnected:",
          payload,
          "state:",
          this.stateManager.getSnapshot(),
        );
        break;
      case "agentConnectionFailed":
        this.handleAgentConnectionFailed();
        break;
      case "waitingForAgent":
        this.handleWaitingForAgent(payload);
        break;
      case "fileUploaded":
        this.handleFileUploaded(payload);
        break;
      case "fileUploadError":
        this.handleFileUploadError(payload);
        break;
      case "twilioMessage":
        // Handle messages from Twilio
        this.messageHandler?.showTypingIndicator(false);
        this.layout.updateAIStatusIndicator("idle");

        // Determine message type based on author
        let messageType = "agent";

        // Check if this is from the AI bot
        if (
          data.author &&
          (data.author.includes("bot@") || data.author.includes("-bot@"))
        ) {
          messageType = "ai";
        }
        // Check if this is a system message
        else if (data.author === "system") {
          messageType = "system";
        }
        // Check if this is from a human agent
        else if (
          data.author &&
          data.author.includes("@") &&
          !data.author.startsWith("client:")
        ) {
          messageType = "agent";
        }

        // Create message object for display
        const twilioMsg = {
          type: messageType,
          content: data.content || "",
          author: data.author,
          timestamp: data.timestamp || Date.now(),
          // Handle media if present - data.media is already an array
          ...(data.media && { media: data.media, hasMedia: true }),
        };

        // Add message to display
        this.messageHandler?.addMessage(twilioMsg);
        this.refreshActiveCallMarker();

        // Play sound only for agent messages (not AI or system)
        if (messageType === "agent") {
          this.audioManager?.playSound("newMessage");
        }
        break;
      case "agentJoined":
        this.handleAgentJoined(payload);
        break;
      case "mode":
        if (payload.mode === "AGENT" || payload.mode === "agent") {
          // Update UI to reflect agent mode
          this.stateManager.set("waitingForAgent", false);
          this.layout.updateInputState();
          this.logger.debug(
            "[Widget] mode switched to AGENT, state:",
            this.stateManager.getSnapshot(),
          );
        }
        break;
      case "status":
        // Handle status messages (grey centered text like "Seth Wright has entered the chat")
        this.messageHandler?.addMessage({
          type: "status",
          content: payload?.content || data.content || "",
        });
        break;
      default:
        this.messageHandler?.showTypingIndicator(false);
        this.layout.updateAIStatusIndicator("idle");
      //console.warn("CTM Chat Widget: Unknown message type from WebSocket:", data);
    }
  }

  handleEngageClick(event) {
    // Prevent click from triggering if close button was clicked
    if (event.target.closest(".engage-close")) {
      return;
    }

    // Track engagement click
    this.gtmTracker.trackEngagement("clicked");

    // Hide engage message
    this.hideEngageMessage();

    // Open the chat
    if (!this.stateManager.get("isOpen")) {
      this.toggleChat();
    }
  }

  handleEngageClose(event) {
    event.stopPropagation(); // Prevent triggering engage click

    // Track engagement close
    this.gtmTracker.trackEngagement("closed");

    // Hide engage message
    this.hideEngageMessage();

    // Set flag to prevent showing again during this session
    this.stateManager.set("engageMessageDismissed", true);
  }

  handleDebugClick(event) {
    event.stopPropagation(); // Don't toggle chat

    // Clear existing timer
    if (this.debugClickTimer) {
      clearTimeout(this.debugClickTimer);
    }

    this.debugClickCount++;

    // Reset counter after 1 second of no clicks
    this.debugClickTimer = setTimeout(() => {
      this.debugClickCount = 0;
    }, 1000);

    // 5 rapid clicks triggers debug copy
    if (this.debugClickCount >= 5) {
      this.debugClickCount = 0;
      clearTimeout(this.debugClickTimer);
      this.copyDebugConfig();
    }
  }

  async copyDebugConfig() {
    const debugInfo = {
      config: this.configManager.get(),
      state: {
        sessionId: this.stateManager.get("sessionId"),
        connectionStatus: this.stateManager.get("connectionStatus"),
        aiEnabled: this.stateManager.get("aiEnabled"),
        isOpen: this.stateManager.get("isOpen"),
        chatMode: this.stateManager.get("chatMode"),
        messageCount: this.messageHandler?.getMessages()?.length || 0,
      },
      agentAvailability: this.configManager.agentAvailability,
      timestamp: new Date().toISOString(),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));

      // Show brief feedback
      const originalText = this.headerTitleEl.textContent;
      this.headerTitleEl.textContent = "Copied!";
      setTimeout(() => {
        this.headerTitleEl.textContent = originalText;
      }, 1000);
    } catch (err) {
      this.logger.error("Failed to copy debug config:", err);
    }
  }

  async copyAsEval() {
    const allMessages = this.messageHandler?.getMessages() || [];

    // Only include messages from the current conversation (after the last chat-ended marker)
    let startIndex = 0;
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i].chatEnded) {
        startIndex = i + 1;
        break;
      }
    }

    const evalMessages = allMessages
      .slice(startIndex)
      .filter((msg) => msg.type === "user" || msg.type === "ai" || msg.type === "agent")
      .map((msg) => ({
        role: msg.type === "user" ? "user" : "assistant",
        content: msg.content,
      }));

    try {
      await navigator.clipboard.writeText(
        JSON.stringify({ type: "ctm-eval-import", messages: evalMessages }, null, 2),
      );

      const originalText = this.headerTitleEl.textContent;
      this.headerTitleEl.textContent = "Copied!";
      setTimeout(() => {
        this.headerTitleEl.textContent = originalText;
      }, 1000);
    } catch (err) {
      this.logger.error("Failed to copy eval:", err);
    }

    this.optionsMenuEl?.classList.remove("visible");
  }

  hideEngageMessage() {
    this.layout.hideEngageMessage();

    // Clear the engage message timer if it's running
    if (this.engageTimer) {
      clearTimeout(this.engageTimer);
      this.engageTimer = null;
    }
  }

  endChat() {
    this.handleEndChat();
  }

  handleEndChat() {
    this.stateManager.set("didEndChat", true);

    // Track chat end - both as action and as end event to match custom events
    this.gtmTracker.trackAction("endChat", {
      timestamp: Date.now(),
    });
    this.gtmTracker.trackChatEnd("user");

    // Dispatch 'action' event for end chat action
    this.dispatchCustomEvent("action", {
      actionType: "endChat",
      timestamp: Date.now(),
    });

    const requestPayload = {
      type: "endChat",
      sessionId: this.stateManager.get("sessionId"),
    };

    // Set 'ended' state so the UI updates immediately
    this.stateManager.set("connectionStatus", "ended");
    this.stateManager.set("lazyConnectionReady", false);

    // Send end chat to server — the server will process it, send chatEnded back,
    // and close the WS with code 1000 (clean close, no auto-reconnect).
    // We don't disconnect here to avoid racing the message delivery.
    if (this.wsManager) {
      this.wsManager.send(requestPayload);
    }
    if (this.suggestionsContainerEl) {
      const baseSuggestions = Array.isArray(this.config?.suggestions)
        ? this.config.suggestions
        : [];
      this.setSuggestions(baseSuggestions);
    }
    this.layout.updateUIBasedOnState();
    this.optionsMenuEl?.classList.remove("visible"); // Close menu
    this.aiStatusIndicatorEl?.remove();
    this.typingIndicatorEl?.remove();

    // Dispatch 'end' event when chat session ends
    this.dispatchCustomEvent("end", {
      sessionEndedBy: "user",
      messagesCount: (this.messageHandler?.getMessages() || []).length,
    });
  }

  startNewChat(initialMessage) {
    // Clear input and show connecting state
    this.layout.clearInput();

    // Reset state for new chat
    this.stateManager.set("connectionStatus", "connecting");
    this.stateManager.set("lazyConnectionReady", false);
    this.stateManager.set("didEndChat", false);
    this.stateManager.set("callId", null);
    this.stateManager.set("sessionId", null);
    this.stateManager.set("firstUserMessageSent", false);

    // Clear the session cookie so a new session is created
    const sessionCookieName = this.getSessionCookieName();
    deleteSessionIdCookie(sessionCookieName);
    deleteCookie(`${sessionCookieName}_active`);

    // Clear UI messages
    this.messageHandler?.clearMessages();

    // Update UI to show connecting state
    this.layout.updateUIBasedOnState();

    // Reconnect and send the initial message
    this.connectWebSocket()
      .then(() => {
        // Explicitly set connected status - the wsManager emits this but ensure stateManager is updated
        this.stateManager.set("connectionStatus", "connected");
        this.layout.updateUIBasedOnState();
        this.layout.updateInputState();

        // Create and send the initial message
        const messageObject = {
          type: "user",
          content: initialMessage,
          timestamp: Date.now(),
        };

        this.messageHandler?.addMessage(messageObject);
        this.updateHeartbeatState();
        this.sendMessageToServer(messageObject);

        // Track as first message
        this.stateManager.set("firstUserMessageSent", true);
        this.gtmTracker.trackMessageSent("user", {
          messageLength: initialMessage.length,
          hasAttachment: false,
          isFirstMessage: true,
        });

        this.dispatchCustomEvent("sent-first", {
          content: initialMessage,
          timestamp: messageObject.timestamp,
        });

        this.dispatchCustomEvent("sent", {
          content: initialMessage,
          timestamp: messageObject.timestamp,
        });
      })
      .catch((error) => {
        this.logger.error("Failed to start new chat:", error);
        this.messageHandler?.addMessage({
          type: "system",
          content: "Failed to start new chat. Please try again.",
        });
      });
  }

  // --- UI Interaction Handlers ---

  // Public method to refresh configuration from KV store
  // Returns a promise that resolves when the config is updated
  refresh() {
    this.logger.info("Refreshing configuration...");

    if (this.isPreviewMode()) {
      return this.fetchConfig().then(() => this.config);
    }

    return new Promise(async (resolve, reject) => {
      // Set up one-time listener for config update
      const configUpdateHandler = (message) => {
        if (message.type === "widgetConfig") {
          clearTimeout(timeout);
          this.logger.info("Config refresh completed");
          resolve(message.payload);
        }
      };

      // Store the handler so we can remove it later
      this._configUpdateHandler = configUpdateHandler;

      // Timeout after 5 seconds
      const timeout = setTimeout(() => {
        this._configUpdateHandler = null;
        reject(new Error("Config refresh timed out after 5 seconds"));
      }, 5000);

      // Send refresh request via WebSocket if connected
      if (this.wsManager && this.wsManager.isConnected()) {
        this.sendMessageToServer({ type: "refreshConfig" }, false);
        this.logger.debug("Sent refresh request to server");
      } else {
        this.logger.warn("WebSocket not connected, cannot refresh config");
        // Try to reconnect and then refresh
        if (this.config.baseWsUrl && this.config.chatId) {
          this.logger.debug("Attempting to connect WebSocket for refresh...");
          try {
            await this.ensureWebSocketConnected({ showError: false });
            await new Promise((resolve) => setTimeout(resolve, 500)); // Small delay to ensure connection is established

            if (this.wsManager && this.wsManager.isConnected()) {
              this.sendMessageToServer({ type: "refreshConfig" }, false);
              this.logger.debug("Sent refresh request after reconnection");
            } else {
              clearTimeout(timeout);
              this._configUpdateHandler = null;
              reject(new Error("Failed to connect WebSocket"));
            }
          } catch (error) {
            clearTimeout(timeout);
            this._configUpdateHandler = null;
            reject(error);
          }
        } else {
          clearTimeout(timeout);
          this._configUpdateHandler = null;
          reject(new Error("WebSocket not configured"));
        }
      }
    });
  }

  toggleChat() {
    const currentIsOpen = this.stateManager.get("isOpen");
    const newIsOpen = !currentIsOpen;
    this.stateManager.set("isOpen", newIsOpen);

    // Track chat toggle event
    this.gtmTracker.trackChatToggle(newIsOpen);

    this.chatWindowEl?.classList.toggle("open", newIsOpen);
    this.bubbleEl?.classList.toggle("open", newIsOpen);
    this.layout.syncChatWindowLayer?.(newIsOpen);
    //console.log("CTM Chat Widget: Toggling chat window:", newIsOpen ? "open" : "closed");
    this.updateActiveStatus(); // Update widget visibility when chat is toggled
    this.updateHeartbeatState();

    if (newIsOpen) {
      this.layout.hideEngageMessage(); // Hide engage message
      clearTimeout(this.timerHideEngage); // Clear any pending hide timer
      this.layout.focusInput(); // Focus input when opened
      this.layout.scrollToBottom(); // Scroll to bottom when opening
      this.optionsMenuEl?.classList.remove("visible"); // Ensure options menu is closed
    } else {
      // If the user was only filling a form (no chat session), reset to the main menu on close
      if (this.isFormOnlyMode()) {
        this.resetToMainMenu();
      }
      // Optionally show engage message again after a delay when closed
      clearTimeout(this.timerShowEngage);
      /* // Example: Reshow engage message 5s after closing
             this.timerShowEngage = setTimeout(() => {
                 if (!this.stateManager.get('isOpen') && this.engageMessageEl) {
                    this.engageMessageEl.classList.add('visible');
                 }
             }, 5000);
             */
    }

    // Save open/closed state only after a session has been materialized.
    if (this.hasMaterializedSession()) {
      this.sendMessageToServer(
        { type: "widgetState", state: newIsOpen ? "open" : "closed" },
        false,
      );
    }

    // on mobile hide the button they can click on the header
    if (isMobile()) {
      if (newIsOpen) {
        this.bubbleEl.style.display = "none";
      } else {
        this.bubbleEl.style.display = "";
      }
    }

    // Dispatch 'open' or 'close' event based on state
    this.dispatchCustomEvent(newIsOpen ? "open" : "close", {
      hasActiveSession: !!this.stateManager.get("sessionId"),
      messagesCount: (this.messageHandler?.getMessages() || []).length,
      trigger: "user", // Indicates user triggered the toggle
    });

    //  console.log("newIsOpen:", newIsOpen, "singleButtonDefault:", this.stateManager.get('singleButtonDefault'));

    if (newIsOpen && this.stateManager.get("singleButtonDefault")) {
      // if we only have one suggestions button visible just click it after openning
      const visibleButtons =
        this.suggestionsContainerEl.querySelectorAll("button");
      //  console.log("visibleButtons:", visibleButtons);
      if (visibleButtons.length === 1) {
        visibleButtons[0].click();
      }
    }
  }

  hasFormDisplayed() {
    const messages = this.messageHandler?.getMessages() || [];
    return messages.some((m) => m.type === "form");
  }

  hasActiveConversation() {
    if (
      this.stateManager.get("callId") ||
      this.stateManager.get("waitingForAgent")
    ) {
      return true;
    }
    const messages = this.messageHandler?.getMessages() || [];
    return messages.some(
      (m) =>
        m.type === "user" ||
        m.type === "ai" ||
        m.type === "agent" ||
        m.role === "user" ||
        m.role === "assistant" ||
        m.twilioMessage,
    );
  }

  isFormOnlyMode() {
    return this.hasFormDisplayed() && !this.hasActiveConversation();
  }

  clearFormMessages() {
    const messages = this.messageHandler?.getMessages() || [];
    const hasForm = messages.some((m) => m.type === "form");
    if (!hasForm) return false;

    this.messageHandler?.filterMessages((m) => m.type !== "form");
    this.formManager?.setCurrentFormConfig(null);
    return true;
  }

  resetToMainMenu() {
    this.optionsMenuEl?.classList.remove("visible");
    this.clearFormMessages();
    this.updateHeaderText(this.config.headerText);
    this.layout.updateUIBasedOnState();
    this.layout.updateInputState();
  }

  handleBackToMenu(e) {
    e?.stopPropagation?.();
    this.resetToMainMenu();
  }

  handleHeaderClose(e) {
    e?.stopPropagation?.();
    if (this.stateManager.get("isOpen")) {
      this.toggleChat();
    }
  }

  async handleSendMessage() {
    const text = this.layout.getInputValue();
    if (!text || this._messageSendInFlight) {
      return;
    }

    this._messageSendInFlight = true;

    try {
      if (this.stateManager.get("connectionStatus") === "ended") {
        if (!this.stateManager.get("callId")) {
          await this.resumeChatWithMessage(text);
          return;
        }
        this.markActiveCall(this.stateManager.get("callId"));
        this.layout.updateUIBasedOnState();
        this.layout.updateInputState();
      }

      if (!this.wsManager?.isConnected()) {
        const connected = await this.ensureWebSocketConnected();
        if (!connected) {
          return;
        }
      }

      const messageObject = ensureClientTurnId({
        type: "user",
        content: text,
        timestamp: Date.now(),
      });

      // Track message sent (check if it's the first message)
      const isFirstMessage = !this.stateManager.get("firstUserMessageSent");
      this.gtmTracker.trackMessageSent("user", {
        messageLength: text.length,
        hasAttachment: this.pendingFiles.length > 0,
        isFirstMessage: isFirstMessage,
      });

      this.messageHandler?.addMessage(messageObject); // Add locally immediately
      this.updateHeartbeatState();
      this.sendMessageToServer(messageObject); // Send via WebSocket

      this.layout.clearInput();
      this.layout.focusInput();
      this.handleInput(); // Update send button state

      // send a sent-first event for the first user sent message
      if (!this.stateManager.get("firstUserMessageSent")) {
        this.stateManager.set("firstUserMessageSent", true);
        // Dispatch 'firstSent' event for the first user message sent
        this.dispatchCustomEvent("sent-first", {
          content: text,
          timestamp: messageObject.timestamp,
        });
      }

      // Dispatch 'sent' event when user sends a message
      this.dispatchCustomEvent("sent", {
        content: text,
        timestamp: messageObject.timestamp,
      });
    } finally {
      this._messageSendInFlight = false;
    }
  }

  handleInputKeypress(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  handleInput() {
    // Delegate to layout which has access to the DOM elements
    this.layout.updateInputState();
    // Signal activity to server (throttled to avoid flooding)
    this.sendActivitySignal();
    // Send typing indicator to server (throttled)
    this.sendTypingSignal();
  }

  sendActivitySignal() {
    const now = Date.now();
    if (now - (this._lastActivitySignal || 0) < 30000) return; // At most once per 30s
    this._lastActivitySignal = now;
    if (this.wsManager?.isConnected()) {
      this.sendMessageToServer({ type: "activity" }, false);
    }
  }

  sendTypingSignal() {
    if (this.stateManager.get("connectionStatus") === "ended") return;
    const now = Date.now();
    if (now - (this._lastTypingSignal || 0) < 3000) return; // At most once per 3s
    this._lastTypingSignal = now;
    if (this.wsManager?.isConnected()) {
      this.sendMessageToServer({ type: "typing" }, false);
    }
  }

  shouldHeartbeatRun() {
    if (!this.wsManager?.isConnected()) {
      return false;
    }
    if (typeof document !== "undefined" && document.hidden) {
      return false;
    }
    return this.stateManager.get("isOpen") || this.hasActiveConversation();
  }

  updateHeartbeatState() {
    if (!this.wsManager) {
      return;
    }
    if (this.shouldHeartbeatRun()) {
      this.wsManager.resumeHeartbeat();
    } else {
      this.wsManager.pauseHeartbeat();
    }
  }

  markActiveCall(callId) {
    if (!callId) {
      return;
    }

    this.stateManager.set("callId", callId);
    this.stateManager.set("didEndChat", false);
    this._validatingActiveCallReconnect = false;

    // Persist evidence of an in-progress CTM chat so the next page load
    // reconnects immediately and queued agent replies are delivered.
    this.refreshActiveCallMarker();

    if (this.stateManager.get("connectionStatus") === "ended") {
      this.stateManager.set("connectionStatus", "connected");
      deleteCookie(`${this.getSessionCookieName()}_ended`);
      this.hideResumeButton();
    }
  }

  getActiveCallMarkerMinutes() {
    const timeoutSeconds = Number(
      this.config?.idleChatTimeout ?? this.config?.idleChatTimer,
    );
    const effectiveTimeoutSeconds =
      Number.isFinite(timeoutSeconds) && timeoutSeconds > 0
        ? timeoutSeconds
        : 300;
    return effectiveTimeoutSeconds / 60;
  }

  refreshActiveCallMarker() {
    if (!this.stateManager.get("callId")) {
      return;
    }

    setCookie(`${this.getSessionCookieName()}_active`, "true", {
      minutes: this.getActiveCallMarkerMinutes(),
    });
  }

  isActiveCallActivityMessage(messageObject) {
    return (
      messageObject?.type === "user" ||
      messageObject?.type === "typing" ||
      messageObject?.type === "activity" ||
      messageObject?.type === "activateAI" ||
      messageObject?.type === "activateHumanAgent"
    );
  }

  hasRenderableSuggestion(suggestion) {
    if (typeof suggestion === "string") {
      return suggestion.trim().length > 0;
    }

    if (!suggestion || typeof suggestion !== "object") {
      return false;
    }

    if (!suggestion.label || !suggestion.type || !suggestion.value) {
      return false;
    }

    if (
      suggestion.conditions &&
      suggestion.conditions.length > 0 &&
      !this.evaluateConditions(suggestion.conditions)
    ) {
      return false;
    }

    return true;
  }

  getRenderableMenuSuggestions() {
    if (!Array.isArray(this.currentSuggestions)) {
      return [];
    }

    return this.currentSuggestions.filter((suggestion) =>
      this.hasRenderableSuggestion(suggestion),
    );
  }

  hasRenderableMenuSuggestions() {
    return this.getRenderableMenuSuggestions().length > 0;
  }

  ensureWelcomeMessage() {
    // A saved inactive session stays lazy and will not receive history on load.
    // Defer the welcome only for an active session that will reconnect.
    const hasActiveSession =
      this.stateManager.get("sessionId") &&
      getCookie(`${this.getSessionCookieName()}_active`) === "true";
    if (hasActiveSession || this.wsManager?.isConnected()) {
      return;
    }

    const messages = this.messageHandler?.getMessages() || [];
    const hasWelcomeMessage = messages.some((message) => message.welcome);
    const hasConversationOrForm = messages.some((message) =>
      message.type === "user" ||
      message.type === "ai" ||
      message.type === "agent" ||
      message.type === "form" ||
      message.role === "user" ||
      message.role === "assistant" ||
      message.twilioMessage,
    );

    if (hasWelcomeMessage || hasConversationOrForm) {
      return;
    }

    const content = this.evaluateWelcomeMessage();
    if (!content) {
      return;
    }

    this.messageHandler?.addMessage({
      type: "status",
      welcome: true,
      content,
    });
  }

  toggleOptionsMenu(event) {
    event.stopPropagation();
    const isOpening = !this.optionsMenuEl?.classList.contains("visible");
    this.optionsMenuEl?.classList.toggle("visible");

    // Populate suggestions when opening menu
    if (isOpening) {
      this.populateMenuSuggestions();
    }
  }

  populateMenuSuggestions() {
    const menuSuggestionsContainer = this.shadowRoot.getElementById(
      "menu-suggestions-container",
    );
    const menuDivider = this.shadowRoot.getElementById("menu-divider");

    if (!menuSuggestionsContainer || !this.currentSuggestions) return;

    // Clear existing suggestions
    menuSuggestionsContainer.innerHTML = "";

    const validSuggestions = this.getRenderableMenuSuggestions();

    const showEndChat = this.hasActiveConversation();
    if (validSuggestions.length > 0) {
      // Add each suggestion as a menu item
      validSuggestions.forEach((suggestion) => {
        const li = document.createElement("li");
        const btn = document.createElement("button");
        btn.textContent =
          typeof suggestion === "string" ? suggestion : suggestion.label;
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.handleSuggestionClick(suggestion);
          this.optionsMenuEl?.classList.remove("visible");
        });
        li.appendChild(btn);
        menuSuggestionsContainer.appendChild(li);
      });

      // Show divider between suggestions and End Chat (only if End Chat is visible)
      menuDivider.style.display = showEndChat ? "block" : "none";
    } else {
      // Hide divider if no suggestions
      menuDivider.style.display = "none";
    }
  }

  handleOutsideClick(event) {
    if (this.optionsMenuEl?.classList.contains("visible")) {
      const clickedInsideMenu = this.optionsMenuEl.contains(event.target);
      const clickedOnToggleButton = this.optionsToggleBtn.contains(
        event.target,
      );
      if (!clickedInsideMenu && !clickedOnToggleButton) {
        this.optionsMenuEl.classList.remove("visible");
      }
    }
  }

  handleResize() {
    clearTimeout(this.debounceResize);
    this.debounceResize = setTimeout(() => {
      this.layout.adjustWindowHeight();
      // Update mobile-specific adjustments if needed
      if (
        !this.isPreviewMode() &&
        isMobile() &&
        this.inputEl === document.activeElement
      ) {
        this.adjustForMobileKeyboard();
      }
    }, 150);
  }

  // Touch event handlers for better mobile interaction
  handleTouchStart(e) {
    this.touchStartTime = Date.now();
    this.touchStartY = e.touches[0].clientY;
  }

  handleTouchEnd(e) {
    const touchDuration = Date.now() - this.touchStartTime;
    const touchEndY = e.changedTouches[0].clientY;
    const touchDistance = Math.abs(touchEndY - this.touchStartY);

    // Only prevent default if it's NOT a tap (to prevent scrolling/other touch gestures)
    // Allow taps to proceed to trigger the click event
    if (touchDuration >= 200 || touchDistance >= 10) {
      e.preventDefault(); // Prevent scrolling or other touch gestures
    }
    // For actual taps (short duration and minimal movement), let the event propagate naturally
  }

  // Adjust chat window when mobile keyboard appears
  adjustForMobileKeyboard() {
    if (this.isPreviewMode()) return;

    const isOpen = this.stateManager.get("isOpen");
    if (!this.chatWindowEl || !isOpen || !isMobile()) return;

    // Check if the input element is actually visible
    const inputArea = this.shadowRoot.querySelector(".chat-input-area");
    if (!this.inputEl || !inputArea) return;

    // Check if input area is hidden (e.g., when a form is displayed)
    const inputAreaStyle = window.getComputedStyle(inputArea);
    if (
      inputAreaStyle.display === "none" ||
      inputAreaStyle.visibility === "hidden"
    ) {
      //console.log('CTM Chat Widget: Input area not visible, skipping keyboard adjustment');
      return;
    }

    // For mobile devices, adjust the chat window height when keyboard is shown
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      const keyboardHeight = window.innerHeight - visualViewport.height;

      // Only adjust if keyboard is actually shown (more than 50px difference)
      if (keyboardHeight > 50) {
        // Store original scroll position
        const messagesContainer = this.messagesContainerEl;
        const wasAtBottom =
          messagesContainer &&
          messagesContainer.scrollHeight -
            messagesContainer.scrollTop -
            messagesContainer.clientHeight <
            50;

        // Calculate the offset from top to account for any browser UI
        const topOffset = visualViewport.offsetTop || 0;

        // Add keyboard-active class for smooth transitions
        this.chatWindowEl.classList.add("keyboard-active");

        // Apply styles to properly position the chat window
        this.chatWindowEl.style.position = "fixed";
        this.chatWindowEl.style.top = `${topOffset}px`;
        this.chatWindowEl.style.height = `${visualViewport.height}px`;
        this.chatWindowEl.style.maxHeight = `${visualViewport.height}px`;
        this.chatWindowEl.style.transform = "translateZ(0)"; // Force GPU acceleration

        // Ensure the input area is visible by adjusting the messages container
        if (messagesContainer) {
          // Calculate available height for messages (total height - header - input area)
          const headerHeight =
            this.shadowRoot.querySelector(".header")?.offsetHeight || 56;
          const inputAreaHeight =
            this.shadowRoot.querySelector(".chat-input-area")?.offsetHeight ||
            56;
          const availableHeight =
            visualViewport.height - headerHeight - inputAreaHeight;

          // Temporarily adjust messages container to ensure input is visible
          messagesContainer.style.maxHeight = `${availableHeight}px`;
          messagesContainer.style.height = `${availableHeight}px`;
        }

        // If we were at bottom, scroll to bottom after adjustment
        if (wasAtBottom) {
          setTimeout(() => this.layout.scrollToBottom(), 100);
        }
      }
    }
  }

  // Reset mobile keyboard adjustments
  resetMobileKeyboardAdjustment() {
    const isOpen = this.stateManager.get("isOpen");
    if (!this.chatWindowEl || !isOpen || !isMobile()) return;

    // Remove keyboard-active class
    this.chatWindowEl.classList.remove("keyboard-active");

    // Reset chat window styles
    this.chatWindowEl.style.position = "";
    this.chatWindowEl.style.top = "";
    this.chatWindowEl.style.height = "";
    this.chatWindowEl.style.maxHeight = "";
    this.chatWindowEl.style.transform = "";
    this.chatWindowEl.style.width = ""; // Ensure width is also reset

    // Reset messages container
    if (this.messagesContainerEl) {
      this.messagesContainerEl.style.height = "";
      this.messagesContainerEl.style.maxHeight = "";
    }

    // Re-apply normal height adjustment after a small delay to ensure class is removed
    setTimeout(() => {
      this.layout.adjustWindowHeight();
    }, 50);

    // Ensure proper scroll position
    setTimeout(() => this.layout.scrollToBottom(), 100);
  }

  // Handle visibility changes for better mobile performance
  async handleVisibilityChange() {
    if (document.hidden) {
      this.updateHeartbeatState();
    } else {
      // Page is visible again — signal activity if chat is open
      this.updateHeartbeatState();
      if (this.stateManager.get("isOpen")) {
        if (
          this.stateManager.get("connectionStatus") === "closed" &&
          !this.wsManager?.isConnected()
        ) {
          await this.ensureWebSocketConnected({ showError: false });
          this.updateHeartbeatState();
        }
        this.sendActivitySignal();
      }
    }
  }

  async _handleFormSuggestion(payload) {
    const visitorSid = await this.resolveCtmVisitorSid();

    // Track form display
    this.gtmTracker.trackFormInteraction("displayed", {
      formId: payload.value,
      formType: payload.form_type || "embedded",
    });

    this.formManager.setCurrentFormConfig({
      ...payload,
      send_to_ai: payload.send_to_ai || false,
      aiEnabled: this.stateManager.get("aiEnabled"),
    });

    const formMessage = this.formManager.createFormMessage(payload);
    formMessage.type = "form";
    formMessage.form = payload.form;
    formMessage.formConfig = payload;
    formMessage.visitorSid = visitorSid;

    this.messageHandler?.addMessage(formMessage);
    this.suggestionsContainerEl.style.display = "none";

    this.dispatchCustomEvent("action", {
      actionType: "formDisplay",
      formPayload: payload,
      timestamp: Date.now(),
    });
  }

  _handleQueueSuggestion(payload, text) {
    // Handle queue routing for human agent
    this.messageHandler?.addMessage({
      type: "system",
      content: "Connecting you to a human agent...",
    });

    // Set waiting state and disable input
    this.stateManager.set("waitingForAgent", true);
    this.updateHeartbeatState();
    this.layout.updateInputState();

    // Send message to activate human agent chat with route parameter
    this.sendMessageToServer({
      type: "activateHumanAgent",
      route: payload.value,
      content: text,
    });

    // Update UI to show we're connecting to an agent
    this.updateHeaderText("Connecting to Agent...");
  }

  /**
   * Build a compact key for guarding one physical suggestion activation from
   * duplicate DOM listeners or overlapping pointer/click events.
   *
   * @param {string|object} payload Suggestion payload.
   * @returns {string} Stable short-lived suggestion key.
   */
  getSuggestionTurnKey(payload) {
    if (typeof payload !== "object" || payload === null) {
      return `text:${String(payload)}`;
    }

    return JSON.stringify([
      payload.type || "text",
      payload.label || "",
      payload.value || "",
    ]);
  }

  /**
   * Record a suggestion activation and report whether the same activation was
   * already seen during the short browser event-coalescing window.
   *
   * @param {string} suggestionTurnKey Stable suggestion key.
   * @returns {boolean} True when this activation is a duplicate.
   */
  isDuplicateSuggestionTurn(suggestionTurnKey) {
    const now = Date.now();
    for (const [key, expiresAt] of this._recentSuggestionTurns) {
      if (expiresAt <= now) {
        this._recentSuggestionTurns.delete(key);
      }
    }

    if ((this._recentSuggestionTurns.get(suggestionTurnKey) || 0) > now) {
      return true;
    }

    this._recentSuggestionTurns.set(
      suggestionTurnKey,
      now + SUGGESTION_DEDUPE_WINDOW_MS,
    );
    return false;
  }

  async handleSuggestionClick(payload) {
    const suggestionTurnKey = this.getSuggestionTurnKey(payload);
    if (this.isDuplicateSuggestionTurn(suggestionTurnKey)) {
      this.logger.debug("Ignoring duplicate suggestion activation", {
        suggestionTurnKey,
      });
      return;
    }

    const text =
      typeof payload == "object"
        ? `${payload.label} - ${payload.value}`
        : payload;
    let sendToAI = true;

    //  console.log("CTM Chat Widget: Handling suggestion click:", text, payload);

    // Track suggestion click
    this.gtmTracker.trackSuggestionClick(payload);

    // Dispatch 'action' event for suggestion clicks
    this.dispatchCustomEvent("action", {
      actionType: "suggestion",
      suggestionText: text,
      suggestionPayload: payload,
      timestamp: Date.now(),
    });

    const messageObject = ensureClientTurnId({
      type: "user",
      content: text,
      timestamp: Date.now(),
      isSuggestion: true,
    });

    if (typeof payload === "object" && payload.type) {
      if (payload.type === "form") {
        await this._handleFormSuggestion(payload);
        return;
      } else if (payload.type == "url") {
        if (this.isPreviewMode()) {
          this.dispatchCustomEvent("preview-navigation", {
            url: payload.value,
          });
        } else {
          window.location = payload.value;
        }
        sendToAI = false;
      } else if (payload.type === "queue") {
        const connected = await this.ensureWebSocketConnected();
        if (!connected) {
          return;
        }
        this._handleQueueSuggestion(payload, text);
        return;
      }
    }
    if (!sendToAI) {
      return;
    }

    const connected = await this.ensureWebSocketConnected();
    if (!connected) {
      return;
    }
    this._handleAISuggestion(messageObject);
  }

  _handleAISuggestion(messageObject) {
    // If AI is not enabled and this suggestion would send to AI, enable it first
    if (!this.stateManager.get("aiEnabled")) {
      // Add the user message to the UI
      this.messageHandler?.addMessage(messageObject);
      this.updateHeartbeatState();

      // Track AI chat start when user clicks suggestion to activate AI (e.g., "Chat with Virtual Assistant")
      this.gtmTracker.trackAction("chatAI", {
        trigger: "ai_activation_suggestion",
        timestamp: Date.now(),
      });

      // Send activation message with the content
      this.sendMessageToServer({
        type: "activateAI",
        content: messageObject.content,
        timestamp: messageObject.timestamp,
        clientTurnId: messageObject.clientTurnId,
      });
      // Focus input with a small delay for mobile devices
      setTimeout(() => {
        this.layout.focusInput();
        // For mobile devices, ensure the keyboard opens
        if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
          const inputEl = this.shadowRoot.getElementById("chat-input");
          inputEl?.click(); // Trigger click to ensure keyboard opens
          // Scroll to bottom to ensure input is visible when keyboard opens
          this.layout.scrollToBottom();
        }
      }, 100);
      this.handleInput();
      // The server will process this message after enabling AI
      return;
    }

    this.messageHandler?.addMessage(messageObject);
    this.updateHeartbeatState();
    this.sendMessageToServer(messageObject);
    // Focus input with a small delay for mobile devices
    setTimeout(() => {
      this.layout.focusInput();
      // For mobile devices, ensure the keyboard opens
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
        const inputEl = this.shadowRoot.getElementById("chat-input");
        inputEl?.click(); // Trigger click to ensure keyboard opens
      }
    }, 100);
    this.handleInput();
  }

  addMessage(messageObject) {
    this.messageHandler?.addMessage(messageObject);
  }

  renderSingleMessage(message) {
    this.messageHandler?.renderSingleMessage(message);
  }

  renderMessages() {
    this.messageHandler?.renderMessages();
  }

  evaluateScheduleCondition(scheduleId) {
    return this.configManager.evaluateScheduleCondition(scheduleId);
  }

  evaluateUrlContainsCondition(value) {
    return this.configManager.evaluateUrlContainsCondition(value);
  }

  evaluateConditions(conditions) {
    return this.configManager.evaluateConditions(conditions);
  }

  /*
    suggestions can be an array of text or objects with text and action properties
    [ {label: "Button Label", action: "actionName"}, "Button Label" , "Another Button"]
  */
  setSuggestions(suggestions = []) {
    if (!this.suggestionsContainerEl) return;

    const newSuggestions = this.configManager.filterSuggestions(suggestions);
    //  console.log("newSuggestions:", newSuggestions);

    if (
      JSON.stringify(this.currentSuggestions) !== JSON.stringify(newSuggestions)
    ) {
      this.currentSuggestions = newSuggestions;
      this.suggestionsContainerEl.innerHTML = "";

      if (Array.isArray(newSuggestions) && newSuggestions.length > 0) {
        newSuggestions.forEach((text) => {
          const type = typeof text;
          if (type === "string" && text.trim()) {
            const btn = document.createElement("button");
            btn.textContent = text;
            btn.setAttribute("part", "suggestion-button");
            btn.addEventListener("click", () =>
              this.handleSuggestionClick(text),
            );
            this.suggestionsContainerEl.appendChild(btn);
          } else if (
            type === "object" &&
            text.label &&
            text.type &&
            text.value
          ) {
            const label = text.label.trim();
            const btn = document.createElement("button");
            btn.textContent = label;
            btn.setAttribute("part", "suggestion-button");
            btn.addEventListener("click", () =>
              this.handleSuggestionClick(text),
            );
            this.suggestionsContainerEl.appendChild(btn);
          }
        });
      } else {
        // Hide the container when there are no suggestions
        this.suggestionsContainerEl.style.display = "none";
      }
      this.layout.adjustWindowHeight();
      this.layout.updateInputState();
    }

    this.scheduleConditionReevaluation();
  }

  scheduleConditionReevaluation() {
    // Clear existing timer
    if (this.suggestionReevaluationTimer) {
      clearTimeout(this.suggestionReevaluationTimer);
    }

    // Check if any suggestions have schedule conditions
    const hasScheduleConditions = this.currentSuggestions.some((suggestion) =>
      suggestion.conditions?.some((condition) => condition.type === "schedule"),
    );

    if (hasScheduleConditions) {
      // Re-evaluate every minute for schedule conditions
      this.suggestionReevaluationTimer = setTimeout(() => {
        //console.log("Re-evaluating suggestion conditions...");
        this.setSuggestions(this.currentSuggestions);
      }, 60000); // 1 minute
    }
  }

  renderMarkdownContent(content) {
    if (!content) return content;

    if (this.marked) {
      try {
        const markedOptions = {
          breaks: true,
          gfm: true,
        };

        const htmlContent = this.marked.parse(content, markedOptions);

        // Sanitize HTML to prevent XSS
        const sanitizedContent = this.sanitizeHTML(htmlContent);
        // Fix ordered list numbering when lists are split by interleaved elements
        const fixedContent = fixOrderedListNumbering(sanitizedContent);
        return fixedContent || content;
      } catch (error) {
        this.logger.error("Error parsing markdown:", error);
        return content;
      }
    }

    return content;
  }

  playNewMessageSound() {
    this.audioManager?.playSound("newMessage");
  }

  handleFormSubmit(formData) {
    // Track form submission
    this.gtmTracker.trackFormInteraction("submitted", {
      formId: formData.formId || null,
      formType: formData.formType || "embedded",
    });

    // Called by FormManager when a form is submitted
    // Start the chat if not already started
    if (!this.stateManager.get("callId")) {
      // The form manager will handle the submission
      // We just need to ensure the chat session is active
    }
  }

  showTypingIndicator(show = true, message = null) {
    this.messageHandler?.showTypingIndicator(show, message);
  }

  scrollToBottom() {
    this.layout.scrollToBottom();
  }

  adjustWindowHeight() {
    this.layout.adjustWindowHeight();
  }

  handleChatEnded(payload) {
    //console.log("Chat ended:", payload);

    // Clear any active indicators and timeouts
    this.messageHandler?.showTypingIndicator(false);
    this.layout.updateAIStatusIndicator("idle");
    clearTimeout(this._aiStatusTimeout);
    this._aiStatusTimeout = null;
    clearTimeout(this._typingHideTimer);
    this._typingHideTimer = null;

    // Update connection status
    this.stateManager.set("connectionStatus", "ended");
    this.stateManager.set("lazyConnectionReady", false);

    // Clear callId since chat has ended
    this.stateManager.set("callId", null);
    const sessionCookieName = this.getSessionCookieName();
    deleteCookie(`${sessionCookieName}_active`);

    // Update widget visibility when chat ends
    this.updateActiveStatus();

    // Set a cookie to track chat ended state with 10-minute expiration
    if (payload.canResume) {
      setCookie(`${sessionCookieName}_ended`, "true", {
        minutes: 10,
      });
      //console.log("Set chat ended cookie with 10-minute expiration");
    }

    if (payload.source === "previous_session") {
      // Reconnect/refresh after chat ended — reset AI to config default
      if (!this._pendingResumeMessage) {
        const configAiEnabled = !!this.config?.aiEnabled;
        this.stateManager.set("aiEnabled", configAiEnabled);

        if (configAiEnabled) {
          // AI available — keep history for context, show "Chat ended" status
          this.messageHandler?.addMessage({
            type: "status",
            content: "Chat ended",
            chatEnded: true,
          });
        } else {
          // No AI (form-only flow) — clear old history/forms for a fresh start
          this.messageHandler?.clearMessages();
        }
      }
    } else {
      // Live chat-end event — show status so user knows what happened
      this.messageHandler?.addMessage({
        type: "status",
        content: payload.reason || "Session ended",
        chatEnded: true,
      });
    }

    // Restore default suggestions (re-evaluates conditions)
    if (this.suggestionsContainerEl) {
      const baseSuggestions = Array.isArray(this.config?.suggestions)
        ? this.config.suggestions
        : [];
      this.setSuggestions(baseSuggestions);
    }

    // Update UI state
    this.layout.updateUIBasedOnState();
  }

  handleChatResumed(payload) {
    //console.log("Chat resumed:", payload);
    const wasRequested = this._resumeRequested;
    this._resumeRequested = false;

    // Clear the chat ended cookie
    deleteCookie(`${this.getSessionCookieName()}_ended`);
    //console.log("Cleared chat ended cookie");

    if (payload.success) {
      // Update connection status
      this.stateManager.set("connectionStatus", "connected");
      this.updateActiveStatus(); // Update widget visibility when chat is resumed

      // Show success message
      if (wasRequested) {
        this.messageHandler?.addMessage({
          type: "status",
          content: "Chat resumed successfully",
        });
      }

      // Hide resume button
      this.hideResumeButton();

      // Update UI state
      this.layout.updateUIBasedOnState();
    } else {
      // Show error message
      if (wasRequested) {
        this.messageHandler?.addMessage({
          type: "system",
          content: `Failed to resume chat: ${payload.error || "Unknown error"}`,
        });
      }
    }
  }

  showResumeButton() {
    // Remove any existing resume button
    this.hideResumeButton();

    // Create resume button container
    const resumeContainer = document.createElement("div");
    resumeContainer.id = "resume-container";
    resumeContainer.style.cssText = `
      padding: 15px;
      text-align: center;
      background: #f0f0f0;
      border-top: 1px solid #ddd;
    `;

    const resumeBtn = document.createElement("button");
    resumeBtn.textContent = "Resume Chat";
    resumeBtn.style.cssText = `
      background: var(--primary-color);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 20px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    `;

    resumeBtn.addEventListener("click", () => {
      //console.log("Resume button clicked");
      this.resumeChat();
    });

    resumeContainer.appendChild(resumeBtn);

    // Insert before input area
    const inputArea = this.shadowRoot.querySelector(".chat-input-area");
    if (inputArea) {
      inputArea.parentNode.insertBefore(resumeContainer, inputArea);
    }
  }

  hideResumeButton() {
    const resumeContainer = this.shadowRoot.getElementById("resume-container");
    if (resumeContainer) {
      resumeContainer.remove();
    }
  }

  async resumeChat() {
    //console.log("Resuming chat...");

    // Clear the ended chat flag
    this.stateManager.set("didEndChat", false);

    // Update UI to show connecting state
    this.stateManager.set("connectionStatus", "connecting");

    // Hide the resume button immediately
    this.hideResumeButton();

    // If WebSocket is not connected, reconnect first
    if (!this.wsManager || !this.wsManager.isConnected()) {
      //console.log("Reconnecting WebSocket for resume...");
      const connected = await this.ensureWebSocketConnected();
      if (!connected) {
        return;
      }
      this._resumeRequested = true;
      this.sendMessageToServer(
        {
          type: "resumeChat",
        },
        false,
      );
    } else {
      // WebSocket is already connected, send resume message immediately
      this._resumeRequested = true;
      this.sendMessageToServer(
        {
          type: "resumeChat",
        },
        false,
      );
    }
  }

  async resumeChatWithMessage(text) {
    this.layout.clearInput();
    this.stateManager.set("didEndChat", false);
    this.stateManager.set("connectionStatus", "connecting");
    this.layout.updateUIBasedOnState();

    const messageObject = ensureClientTurnId({
      type: "user",
      content: text,
      timestamp: Date.now(),
    });

    if (!this.wsManager || !this.wsManager.isConnected()) {
      // Need to reconnect — initialize() will send history which clears the UI,
      // so defer the user message until the history event arrives.
      this._pendingResumeMessage = messageObject;
      const connected = await this.ensureWebSocketConnected();
      if (connected) {
        this.stateManager.set("connectionStatus", "connected");
        this.layout.updateUIBasedOnState();
        this.layout.updateInputState();
        this._resumeRequested = true;
        this.sendMessageToServer({ type: "resumeChat" }, false);
      }
    } else {
      // WS already connected (auto-reconnected) — no history event coming,
      // so send the message directly.
      this.stateManager.set("connectionStatus", "connected");
      this.layout.updateUIBasedOnState();
      this.layout.updateInputState();
      this._resumeRequested = true;
      this.sendMessageToServer({ type: "resumeChat" }, false);
      this.messageHandler?.addMessage(messageObject);
      this.updateHeartbeatState();
      this.sendMessageToServer(messageObject);
    }
  }

  handleAgentConnected(payload) {
    //console.log("Agent connected:", payload);

    // Clear waiting state and re-enable input
    this.stateManager.set("waitingForAgent", false);
    this.layout.updateInputState();

    // Note: Removed status message - now using event display instead

    // Update UI to show we're now in agent mode
    this.layout.updateUIBasedOnState();
    this.updateActiveStatus(); // Update widget visibility when agent connects

    // Play sound for agent connection
    this.audioManager?.playSound("newMessage");
  }

  updateHeaderText(text) {
    this.layout.updateHeaderText(text);
  }

  handleAgentConnectionFailed() {
    //console.log("Agent connection failed");

    // Clear waiting state and re-enable input
    this.stateManager.set("waitingForAgent", false);
    this.layout.updateInputState();

    // Reset header text
    this.updateHeaderText(this.config.headerText);

    // Update UI state
    this.layout.updateUIBasedOnState();
  }

  handleAgentJoined(payload) {
    //console.log("Agent joined conversation:", payload);

    // Clear waiting state
    this.stateManager.set("waitingForAgent", false);
    this.layout.updateInputState();

    // Update header to show agent name
    const agentName = payload.agent?.name || "Agent";
    this.updateHeaderText(`Chat with ${agentName}`);

    // Note: Removed status message - now using event display instead

    // Update UI state
    this.layout.updateUIBasedOnState();

    // Play notification sound
    this.audioManager?.playSound("newMessage");
  }

  handleWaitingForAgent(payload) {
    //console.log("Handling waiting for agent state:", payload);

    if (payload.waiting) {
      // Set waiting state and disable input
      this.stateManager.set("waitingForAgent", true);
      this.updateHeartbeatState();
      this.layout.updateInputState();

      // Hide AI status indicator when transferring
      this.layout.updateAIStatusIndicator("idle");
    } else {
      // Clear waiting state
      this.stateManager.set("waitingForAgent", false);
      this.updateHeartbeatState();
      this.layout.updateInputState();
    }
  }

  resetConnection() {
    //console.log("CTM Chat Widget: Resetting connection...");
    this.stateManager.set("initialConnectionAttempted", false); // Allow re-attempt
    this.stateManager.set("connectionStatus", "disconnected");
    this.stateManager.set(
      "lazyConnectionReady",
      !!(this.config.baseWsUrl && this.config.chatId),
    );

    // Reset WebSocket
    if (this.wsManager) {
      this.wsManager.disconnect(1000, "Connection reset");
    }

    this.messageHandler?.clearMessages(); // Clear messages on reset
    this.layout.updateUIBasedOnState(); // Reflect disconnected state
    // Show disconnected message
    /*this.addMessage({
      type: "system",
      content: "Chat connection reset.",
    });*/
  }

  // --- File Upload Methods ---

  enableAttachments() {
    this.stateManager.set("attachmentsEnabled", true);
  }

  disableAttachments() {
    this.stateManager.set("attachmentsEnabled", false);
  }

  handleAttachClick() {
    if (this.fileInputEl) {
      this.fileInputEl.click();
    }
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      this.handleFiles(files);
    }
    // Reset input so the same file can be selected again
    event.target.value = "";
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    // Add visual feedback
    this.messagesContainerEl.classList.add("drag-over");
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.messagesContainerEl.classList.remove("drag-over");

    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length > 0) {
      this.handleFiles(imageFiles);
    }
  }

  async handleFiles(files) {
    let connected = null;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        this.messageHandler?.addMessage({
          type: "system",
          content: `File "${file.name}" is too large. Maximum size is 10MB.`,
        });
        continue;
      }

      // Track file upload
      this.gtmTracker.trackFileUpload(file.type, file.size);

      if (connected === null) {
        connected = await this.ensureWebSocketConnected();
      }
      if (!connected) {
        return;
      }

      // Upload file directly to R2
      try {
        // Create a preview message using local URL
        const messageId = `file_${Date.now()}_${Math.random()}`;
        const localPreview = URL.createObjectURL(file);

        this.messageHandler?.addMessage({
          id: messageId,
          type: "user",
          content: `[Uploading image: ${file.name}]`,
          file: {
            name: file.name,
            type: file.type,
            size: file.size,
            preview: localPreview,
          },
        });

        // Get auth token before upload
        const token = await this.getSessionToken();

        // Create FormData for upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("sessionId", this.stateManager.get("sessionId"));
        formData.append("doId", this.getDurableObjectId());

        // Upload directly to the server with authentication
        const uploadResponse = await fetch(
          `${this.config.baseApiUrl}/api/upload`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!uploadResponse.ok) {
          const error = await uploadResponse.json();
          throw new Error(error.error || "Upload failed");
        }

        const { url, securePath } = await uploadResponse.json();

        // Update the message with the uploaded URL
        const messages = this.messageHandler?.getMessages() || [];
        const message = messages.find((m) => m.id === messageId);
        if (message) {
          message.content = `[Image: ${file.name}]`;
          const secureUrl = securePath
            ? this.buildSecureMediaUrl(securePath)
            : null;
          message.file = message.file || {};
          message.file.publicUrl = url;
          message.file.securePath = securePath;
          message.file.secureUrl = secureUrl;
          message.file.url = secureUrl || url;
          URL.revokeObjectURL(localPreview); // Clean up local preview
          this.messageHandler?.renderMessages();
        }
      } catch (error) {
        this.logger.error("Error uploading file:", error);
        this.messageHandler?.addMessage({
          type: "system",
          content: `Failed to upload "${file.name}": ${error.message}`,
        });
      }
    }
  }

  async fetchFreshMediaUrl(mediaSid) {
    try {
      // Get necessary parameters
      const chatId = this.config.chatId;
      const accountId = this.accountId;
      const region = this.region;
      const sessionId = this.stateManager.get("sessionId");

      if (!chatId || !accountId || !region || !sessionId) {
        this.logger.error("Missing required parameters for media fetch");
        return null;
      }

      const url = `${this.config.baseApiUrl}/api/media/${sessionId}/${mediaSid}?chatId=${chatId}&accountId=${accountId}&region=${region}`;

      const response = await fetch(url);
      if (!response.ok) {
        this.logger.error("Failed to fetch media URL:", response.status);
        return null;
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      this.logger.error("Error fetching fresh media URL:", error);
      return null;
    }
  }

  getDurableObjectId() {
    // Use the actual DO ID from server if available
    const durableObjectId = this.stateManager.get("durableObjectId");
    if (durableObjectId) {
      return durableObjectId;
    }

    // Fallback: Generate the Durable Object ID string from the connection parameters
    const region = this.config.region || "useast";
    const chatId = this.config.chatId;
    const accountId = this.config.accountId;
    const sessionId = this.stateManager.get("sessionId");

    const generatedId = `${region}.${chatId}.${accountId}.${sessionId}`;
    this.logger.warn(
      `Using generated DO ID (may not match server): ${generatedId}`,
    );
    return generatedId;
  }

  async getSessionToken() {
    // If we have a valid token, return it
    const sessionToken = this.stateManager.get("sessionToken");
    const sessionTokenExpiry = this.stateManager.get("sessionTokenExpiry");
    if (sessionToken && sessionTokenExpiry > Date.now()) {
      return sessionToken;
    }

    // Request a new token
    try {
      const response = await fetch(
        `${this.config.baseApiUrl}/api/auth/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: this.stateManager.get("sessionId"),
            widgetId: this.config.chatId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to get session token");
      }

      const { token } = await response.json();
      this.stateManager.set("sessionToken", token);
      // Set expiry slightly before actual expiry to avoid edge cases
      this.stateManager.set("sessionTokenExpiry", Date.now() + 55 * 60 * 1000); // 55 minutes
      return token;
    } catch (error) {
      this.logger.error("Error getting session token:", error);
      throw error;
    }
  }

  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  buildSecureMediaUrl(securePath) {
    if (!securePath) return null;
    const base = this.config?.baseApiUrl || "";
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const path = securePath.startsWith("/") ? securePath : `/${securePath}`;
    const rawUrl = normalizedBase ? `${normalizedBase}${path}` : path;

    // Append session token for authenticated media fetches
    const token = this.stateManager?.get("sessionToken");
    if (
      !token &&
      path.startsWith("/api/media/") &&
      !this.pendingMediaTokenPromise
    ) {
      // Lazily fetch a token so we can re-render with secured URLs
      this.pendingMediaTokenPromise = this.getSessionToken?.()
        .catch(() => null)
        .finally(() => {
          this.pendingMediaTokenPromise = null;
          this.messageHandler?.renderMessages();
        });
    }
    if (token && path.startsWith("/api/media/")) {
      try {
        const urlObj = new URL(
          rawUrl,
          typeof window !== "undefined"
            ? window.location.origin
            : "https://dummy",
        );
        urlObj.searchParams.set("token", token);
        return urlObj.toString();
      } catch (err) {
        const separator = rawUrl.includes("?") ? "&" : "?";
        return `${rawUrl}${separator}token=${encodeURIComponent(token)}`;
      }
    }

    return rawUrl;
  }

  decorateFileMetadata(file) {
    if (!file) return null;
    const decorated = { ...file };
    if (decorated.securePath && !decorated.secureUrl) {
      decorated.secureUrl = this.buildSecureMediaUrl(decorated.securePath);
    }
    if (decorated.secureUrl) {
      decorated.publicUrl = decorated.publicUrl || decorated.url;
      decorated.url = decorated.secureUrl;
    }
    return decorated;
  }

  handleFileUploaded(payload) {
    const secureUrl = payload.securePath
      ? this.buildSecureMediaUrl(payload.securePath)
      : null;
    const messages = this.messageHandler?.getMessages() || [];
    let message = payload.messageId
      ? messages.find((m) => m.id === payload.messageId)
      : null;

    // Fallback: create a new message if the original can't be found
    if (!message) {
      const newId = payload.messageId || `file_${Date.now()}`;
      const content = payload.fileName
        ? `[Image: ${payload.fileName}]`
        : "File uploaded";
      this.messageHandler?.addMessage({
        id: newId,
        type: "user",
        content,
        timestamp: Date.now(),
        file: {
          name: payload.fileName,
          publicUrl: payload.url,
          securePath: payload.securePath,
          secureUrl,
          url: secureUrl || payload.url,
        },
      });
      return;
    }

    message.file = message.file || {};
    message.file.name = message.file.name || payload.fileName;
    message.file.publicUrl = payload.url;
    message.file.securePath = payload.securePath;
    message.file.secureUrl = secureUrl;
    message.file.url = secureUrl || payload.url;
    this.messageHandler?.renderMessages();
  }

  handleFileUploadError(payload) {
    // Find the message with the matching ID and show error
    const messages = this.messageHandler?.getMessages() || [];
    const message = messages.find((m) => m.id === payload.messageId);
    if (message) {
      // Update the message to show error
      const messageEl = this.messagesContainerEl.querySelector(
        `[data-message-id="${payload.messageId}"]`,
      );
      if (messageEl) {
        messageEl.innerHTML = "";
        messageEl.textContent = `Failed to upload ${message.file.name}: ${payload.error}`;
        messageEl.style.color = "#dc2626";
      }
    }
  }

  // Schedule visibility methods

  applyScheduleVisibility() {
    const currentScheduleVisibility =
      this.stateManager.get("scheduleVisibility") || {};
    const scheduleInfo = this.config._scheduleVisibility ||
      (currentScheduleVisibility.hasSchedule
        ? {
            isVisible: currentScheduleVisibility.isVisible,
            nextTransition: currentScheduleVisibility.nextTransition,
            hasSchedule: currentScheduleVisibility.hasSchedule,
          }
        : null);

    if (!scheduleInfo) {
      // No schedule config, show widget by default
      this.showWidget();
      return;
    }

    this.stateManager.update({
      "scheduleVisibility.hasSchedule": scheduleInfo.hasSchedule,
      "scheduleVisibility.nextTransition": scheduleInfo.nextTransition,
    });

    // Check if widget should be visible
    if (scheduleInfo.hasSchedule) {
      this.stateManager.set(
        "scheduleVisibility.isVisible",
        scheduleInfo.isVisible,
      );

      // Apply visibility unless there's an active chat
      if (!scheduleInfo.isVisible && !this.hasActiveChat()) {
        this.hideWidget();
      } else {
        this.showWidget();
      }

      // Schedule next visibility check
      if (scheduleInfo.nextTransition) {
        this.scheduleNextVisibilityCheck();
      }
    } else {
      // No schedule defined, show widget
      this.showWidget();
    }
  }

  hasActiveChat() {
    // Check various indicators of an active chat session
    // 1. Chat window is open
    const isOpen = this.stateManager.get("isOpen");
    if (isOpen) {
      return true;
    }

    // 2. Has messages (excluding just the welcome message)
    const messages = this.messageHandler?.getMessages() || [];
    const hasUserMessages = messages.some(
      (m) =>
        m.type === "user" ||
        m.type === "agent" ||
        (m.type === "ai" && !m.welcome),
    );
    if (hasUserMessages) {
      return true;
    }

    // 3. Active WebSocket connection with ongoing conversation
    const connectionStatus = this.stateManager.get("connectionStatus");
    const callId = this.stateManager.get("callId");
    if (connectionStatus === "connected" && callId) {
      return true;
    }

    // 4. Waiting for agent
    const waitingForAgent = this.stateManager.get("waitingForAgent");
    if (waitingForAgent) {
      return true;
    }

    return false;
  }

  hideWidget() {
    //console.log("CTM Chat Widget: Hiding widget due to schedule");

    // Add schedule-hidden class to host element (but keep initialized class)
    this.classList.add("schedule-hidden");

    // Clear engage message timer
    if (this.timerShowEngage) {
      clearTimeout(this.timerShowEngage);
      this.timerShowEngage = null;
    }

    // Hide engage message if visible
    if (
      this.engageMessageEl &&
      this.engageMessageEl.classList.contains("visible")
    ) {
      this.engageMessageEl.classList.remove("visible");
    }

    // Close chat window if open (unless there's an active chat)
    const isOpen = this.stateManager.get("isOpen");
    if (isOpen && !this.hasActiveChat()) {
      this.toggleChat();
    }
  }

  showWidget() {
    //console.log("CTM Chat Widget: Showing widget - within scheduled hours");

    // Add initialized class to make widget visible
    this.classList.add("initialized");

    // Remove schedule-hidden class
    this.classList.remove("schedule-hidden");

    this.style.visibility = "visible"; // Make visible after loading libraries

    // Re-enable engage message timer if chat is not open
    const isOpen = this.stateManager.get("isOpen");
    if (!isOpen && this.engageMessageEl) {
      // Clear any existing timer
      if (this.timerShowEngage) {
        clearTimeout(this.timerShowEngage);
      }

      // Show engage message after a delay (only if not dismissed)
      if (!this.stateManager.get("engageMessageDismissed")) {
        this.timerShowEngage = setTimeout(
          () => {
            const currentIsOpen = this.stateManager.get("isOpen");
            if (
              !currentIsOpen &&
              this.engageMessageEl &&
              !this.stateManager.get("engageMessageDismissed")
            ) {
              this.engageMessageEl.classList.add("visible");
              this.engageMessageEl.style.pointerEvents = "auto";
            }
          },
          (this.config.engageMessageTimer || 8) * 1000,
        ); // Use config timer or default to 8 seconds
      }
    }
  }

  scheduleNextVisibilityCheck() {
    // Clear any existing timer
    const checkInterval = this.stateManager.get(
      "scheduleVisibility.checkInterval",
    );
    if (checkInterval) {
      clearTimeout(checkInterval);
    }

    const nextTransition = this.stateManager.get(
      "scheduleVisibility.nextTransition",
    );
    if (!nextTransition) {
      return;
    }

    const now = Date.now();
    const transitionTime = nextTransition;
    const delay = transitionTime - now;

    // Only schedule if transition is in the future
    if (delay > 0) {
      //console.log(`CTM Chat Widget: Scheduling visibility check in ${Math.round(delay / 1000 / 60)} minutes`);

      const timeoutId = setTimeout(() => {
        //console.log("CTM Chat Widget: Visibility transition time reached, refetching config");

        // Refetch config to get updated visibility and next transition
        this.fetchConfig().catch((error) => {
          this.logger.error(
            "Failed to refetch config for schedule update:",
            error,
          );

          // Retry in 5 minutes if fetch fails
          const retryTimeoutId = setTimeout(
            () => {
              this.fetchConfig();
            },
            5 * 60 * 1000,
          );
          this.stateManager.set(
            "scheduleVisibility.checkInterval",
            retryTimeoutId,
          );
        });
      }, delay);
      this.stateManager.set("scheduleVisibility.checkInterval", timeoutId);
    } else {
      //console.log("CTM Chat Widget: Next transition time is in the past, refetching config immediately");
      // Transition time is in the past, refetch config immediately
      this.fetchConfig();
    }
  }

  // Update the class toggle when chat has activity
  updateActiveStatus() {
    if (this.hasActiveChat()) {
      this.classList.add("has-active-chat");
    } else {
      this.classList.remove("has-active-chat");
    }
  }

  evaluateWelcomeMessage() {
    return this.configManager.evaluateWelcomeMessage();
  }

  isWithinSchedule(schedule) {
    return this.configManager.isWithinSchedule(schedule);
  }
}

if (!customElements.get("ctm-chat")) {
  customElements.define("ctm-chat", ChatWidget);
}
